import {
  CSSProperties,
  MouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  CheckCircleOutlined,
  CalendarOutlined,
  CheckOutlined,
  DeleteOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  DownOutlined,
  EditOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  MenuOutlined,
  LinkOutlined,
  RightOutlined,
  SearchOutlined
} from "@ant-design/icons";
import { Button, Checkbox, Dropdown, Select, Switch, Tooltip } from "antd";
import CIQDatePicker from "components/custom-date-picker";
import emptyBidPackagesIllustration from "assets/svg/planner-empty-bid-packages.svg";
import dayjs from "utils/dayjs-config";
import type { Dayjs } from "dayjs";
import {
  DateUtils,
  DATE_FORMAT_MMDDYYYY,
  DATE_FORMAT_YYYYMMDD
} from "utils/dateutils";
import {
  BidPackageRow,
  BidPackageRowGeometry,
  BidWorkflowStatus,
  Geometry,
  MilestoneNode
} from "pages/design-package-details-page/components/view-dependencies/model";
import { RenderConfig } from "pages/design-package-details-page/components/view-dependencies/render-config";
import {
  placementForDate,
  type AddMilestonePlacement
} from "pages/planning-workspace/design-package-milestone-update";
import {
  isActualDateDisabled,
  isActualDateEditable,
  isCommittedDateDisabled
} from "./milestone-date-rules";
import { usePlannerPortalTarget } from "./planner-portal";
import { useTimelineViewPref } from "./timeline-view-prefs";
import DeleteDesignPackageMilestoneModal, {
  type DeleteMilestoneOptions
} from "./delete-design-package-milestone-modal";
import PlannerBidDetailDrawer, {
  BID_DETAIL_PANEL_W
} from "./planner-bid-detail-drawer";
import {
  buildPlannerTimeScale,
  dayOfIso,
  fullTimelineTicks,
  halfMonthTickDays,
  isoOfDay,
  type OverflowZone,
  type PlannerTimeScale
} from "./planner-time-scale";
import BidPackageRequiredOnSite from "../bid-package-required-on-site";
import { bidPackageLabel } from "pages/planning-workspace/bid-package-label";
import { bidPackageDetailsPath } from "pages/planning-workspace/bid-package-path";

const LABEL_W = 250;

type BidSort = "earliest" | "latest" | "numberAsc" | "numberDesc";

const BID_SORTS: Array<{
  id: BidSort;
  label: string;
  descending: boolean;
}> = [
  { id: "earliest", label: "Sort Ascending Start Dates", descending: false },
  { id: "latest", label: "Sort Descending Start Dates", descending: true },
  { id: "numberAsc", label: "Sort Ascending Bid number", descending: false },
  { id: "numberDesc", label: "Sort Descending Bid number", descending: true }
];

type MenuTab = "filter" | "sort";

/** Planned date of the first bid-package milestone (lowest sequence). */
function firstMilestonePlannedDay(g: BidPackageRowGeometry): string | null {
  const first = g.row.milestones.reduce<MilestoneNode | null>(
    (best, milestone) =>
      !best || milestone.sequenceNo < best.sequenceNo ? milestone : best,
    null
  );
  const day = first?.plannedDate?.slice(0, 10);
  return day || null;
}

function compareBidRows(
  a: BidPackageRowGeometry,
  b: BidPackageRowGeometry,
  sort: BidSort
): number {
  const byNumber = a.row.number.localeCompare(b.row.number, undefined, {
    numeric: true,
    sensitivity: "base"
  });
  if (sort === "numberAsc") return byNumber;
  if (sort === "numberDesc") return -byNumber;
  const da = firstMilestonePlannedDay(a);
  const db = firstMilestonePlannedDay(b);
  if (da && db && da !== db) {
    return sort === "earliest" ? da.localeCompare(db) : db.localeCompare(da);
  }
  if (da && !db) return -1;
  if (!da && db) return 1;
  return byNumber;
}

function BidPackageMenu(props: {
  tab: MenuTab;
  onTab: (tab: MenuTab) => void;
  sort: BidSort;
  onSort: (sort: BidSort) => void;
  packages: Array<{ id: string; label: string }>;
  hiddenIds: ReadonlySet<string>;
  onTogglePackage: (id: string) => void;
  onToggleAll: (ids: string[]) => void;
}) {
  const {
    tab,
    onTab,
    sort,
    onSort,
    packages,
    hiddenIds,
    onTogglePackage,
    onToggleAll
  } = props;
  const [packageQuery, setPackageQuery] = useState("");
  const q = packageQuery.trim().toLowerCase();
  const listed = q
    ? packages.filter((item) => item.label.toLowerCase().includes(q))
    : packages;
  const listedIds = listed.map((item) => item.id);
  const allChecked =
    listedIds.length > 0 && listedIds.every((id) => !hiddenIds.has(id));
  const someChecked = listedIds.some((id) => !hiddenIds.has(id)) && !allChecked;
  // The menu's own border is the selected tab's outer edge; a second stroke
  // there would double it.
  const tabStyle = (
    selected: boolean,
    edge: "left" | "right"
  ): CSSProperties =>
    selected
      ? {
          border: "1px solid var(--ciq-gray-400)",
          [edge === "left" ? "borderLeftWidth" : "borderRightWidth"]: 0,
          borderBottomColor: "var(--ciq-planner-foot)",
          background: "var(--ciq-planner-foot)",
          marginBottom: -1
        }
      : {
          border: "1px solid transparent",
          borderBottom: "1px solid var(--ciq-gray-400)",
          background: "var(--ciq-white)"
        };
  const optionClass = [
    "flex h-7 w-full items-center px-2 text-[13px] leading-5 text-ciq-text-900",
    "[&_.ant-checkbox+span]:min-w-0 [&_.ant-checkbox+span]:truncate [&_.ant-checkbox]:top-0",
    "[&_.ant-checkbox-inner]:!h-4 [&_.ant-checkbox-inner]:!w-4 [&_.ant-checkbox-inner]:!rounded-[3px]",
    "[&_.ant-checkbox-inner]:!border [&_.ant-checkbox-inner]:!border-solid [&_.ant-checkbox-inner]:!border-ciq-gray-400 [&_.ant-checkbox-inner]:!bg-white",
    "[&_.ant-checkbox:hover_.ant-checkbox-inner]:!border-ciq-gray-400",
    "[&_.ant-checkbox-checked_.ant-checkbox-inner]:!border-ciq-text-900 [&_.ant-checkbox-checked_.ant-checkbox-inner]:!bg-ciq-text-900",
    "[&_.ant-checkbox-checked_.ant-checkbox-inner::after]:!border-white",
    "[&_.ant-checkbox-checked:hover_.ant-checkbox-inner]:!border-ciq-text-900",
    "[&_.ant-checkbox-indeterminate_.ant-checkbox-inner]:!border-ciq-text-900 [&_.ant-checkbox-indeterminate_.ant-checkbox-inner]:!bg-ciq-text-900",
    "[&_.ant-checkbox-indeterminate_.ant-checkbox-inner::after]:!bg-white"
  ].join(" ");

  return (
    <div
      data-testid="planner-bp-sort-menu"
      className="w-[240px] overflow-hidden rounded-lg border border-solid border-ciq-gray-400 bg-ciq-planner-foot shadow-[0_1px_4px_rgba(0,0,0,0.15)]"
    >
      <div className="flex bg-white">
        <button
          type="button"
          data-testid="planner-bp-menu-filter"
          aria-label="Filter"
          aria-pressed={tab === "filter"}
          className="flex h-10 flex-1 cursor-pointer items-center justify-center text-base text-ciq-text-900"
          style={tabStyle(tab === "filter", "left")}
          onClick={() => onTab("filter")}
        >
          <FilterOutlined className="text-xs text-ciq-text-900 hover:text-ciq-text-900" />
        </button>
        <button
          type="button"
          data-testid="planner-bp-menu-sort"
          aria-label="Sort"
          aria-pressed={tab === "sort"}
          className="flex h-10 flex-1 cursor-pointer items-center justify-center text-base text-ciq-text-900"
          style={tabStyle(tab === "sort", "right")}
          onClick={() => onTab("sort")}
        >
          <MenuOutlined className="text-xs text-ciq-text-900 hover:text-ciq-text-900" />
        </button>
      </div>
      {tab === "filter" ? (
        <div data-testid="planner-bp-filter-panel">
          <div className="px-2 pb-1 pt-2">
            <input
              data-testid="planner-bp-filter-search"
              className="h-8 w-full rounded border border-solid border-ciq-gray-400 bg-white px-2 text-[13px] text-ciq-planner-ink outline-none placeholder:text-ciq-planner-ink4"
              placeholder="Search..."
              value={packageQuery}
              onChange={(event) => setPackageQuery(event.target.value)}
            />
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            <Checkbox
              className={optionClass}
              checked={allChecked}
              indeterminate={someChecked}
              data-testid="planner-bp-filter-all"
              onChange={() => onToggleAll(listedIds)}
            >
              (Select All)
            </Checkbox>
            {listed.map((item) => (
              <Checkbox
                key={item.id}
                className={optionClass}
                checked={!hiddenIds.has(item.id)}
                data-testid={`planner-bp-filter-${item.id}`}
                onChange={() => onTogglePackage(item.id)}
              >
                <span className="truncate" title={item.label}>
                  {item.label}
                </span>
              </Checkbox>
            ))}
          </div>
        </div>
      ) : (
        <div className="py-1" data-testid="planner-bp-sort-panel">
          {BID_SORTS.map((option, index) => {
            const selected = option.id === sort;
            return (
              <div key={option.id}>
                {index === 2 ? (
                  <div className="my-1 h-px bg-ciq-gray-300" />
                ) : null}
                <button
                  type="button"
                  data-testid={`planner-bp-sort-${option.id}`}
                  className={`flex h-8 w-full cursor-pointer items-center gap-2 border-none px-3 text-left text-[13px] leading-5 ${
                    selected
                      ? "bg-ciq-primary-bg font-medium text-ciq-planner-accent"
                      : "bg-transparent text-black/90"
                  }`}
                  onClick={() => onSort(option.id)}
                >
                  {option.descending ? (
                    <ArrowDownOutlined className="text-xs text-ciq-text-900" />
                  ) : (
                    <ArrowUpOutlined className="text-xs text-ciq-text-900" />
                  )}
                  {option.label}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Milestone header, top to bottom: the upper and lower label rows (name, 4px,
// committed date), 24px, the duration tag, 8px, the issued band, then the
// month axis.
const MS_LABEL_TOP = 6;
const MS_LABEL_BOTTOM = 40;
const MS_LABEL_NAME_H = 14;
const MS_LABEL_DATE_H = 12;
const MS_LABEL_GAP = 4;
/** Thick bar beside a milestone's name and date. */
const MS_LABEL_BAR_H = 46;
/** One duration row 24px under the lower label row. */
const OFFSET_TOP =
  MS_LABEL_BOTTOM + MS_LABEL_NAME_H + MS_LABEL_GAP + MS_LABEL_DATE_H + 24;
const OFFSET_H = 20;
/** Issued band, 8px under the duration row. */
const BAND_TOP = OFFSET_TOP + OFFSET_H + 8;
const BAND_H = 24;
const MS_HEAD_H = BAND_TOP + BAND_H;
/** Leaves room under the Bid Packages search box. */
const CHART_H = MS_HEAD_H + 76;
const BID_ROW_H = 131;
/** Required on Site header row (18px days tag + 1px gap) above its card. */
const ROS_HEAD_H = 19;
/** Bid card height. */
const CARD_H = 52;
/** Card top. Above it: the Projected End arrow and the float bracket; below
 *  it: the Deadline label. */
const CARD_DY = 48;
/** Required on Site card height (its own component draws it at 46px). */
const ROS_CARD_H = 46;
/** y of the float bracket inside a row. */
const FLOAT_Y = CARD_DY - 9;
/** Deadline and Projected End lines are both this tall. */
const MARKER_H = 106;
/** Marker lines end near the bottom of the row. */
const MARKER_BOTTOM = BID_ROW_H - 4;
/** Marker lines start here, MARKER_H above their end. */
const MARKER_TOP = MARKER_BOTTOM - MARKER_H;
/** Row of the bid package open in the detail drawer (#F0F2FF). */
const SELECTED_ROW_BG = "bg-ciq-planner-accent-bg";
const HOVER_ROW_BG = "bg-ciq-neutral-25";
/** Prefix of every bid card's test id; the row id follows it. */
const BID_BAR_TESTID = "planner-bp-bar-";
/** Its bid card: 2px accent border with a 3px accent halo at 25%. */
const SELECTED_CARD =
  "!border-2 !border-solid !border-ciq-planner-accent ring-[3px] ring-ciq-planner-accent/25";
/** Milestone pins sit in the middle of the issued band. */
const PIN_Y = BAND_TOP + BAND_H / 2;
const CHIP_Y = MS_HEAD_H + 38;
const OFFSET_FULL_W = 76;
const OFFSET_SHORT_W = 36;

type OffsetFit = "full" | "short" | "pin";

function offsetFit(gap: number): OffsetFit {
  if (gap >= OFFSET_FULL_W + 28) return "full";
  if (gap >= OFFSET_SHORT_W + 16) return "short";
  return "pin";
}

function offsetText(days: number, fit: OffsetFit): string {
  if (fit === "full") return `${days} ${days === 1 ? "day" : "days"}`;
  return `${days}d`;
}

const MS_LABEL_MAX_W = 160;
/** Lead before the first milestone when nothing is drawn earlier: room for
 *  its label (drawn to the left of the dot, at most MS_LABEL_MAX_W - 16 wide
 *  plus its hover padding) and a small gap. */
const TIMELINE_EMPTY_LEAD_PX = MS_LABEL_MAX_W + 16;
/** Trail after the last milestone when nothing is drawn later: room for the
 *  half of its axis date and link chip that sit right of the dot, and a gap. */
const TIMELINE_EMPTY_TRAIL_PX = 48;

type MilestoneLabelText = { x: number; name: string; sub: string };

let labelMeasureCtx: CanvasRenderingContext2D | null | undefined;

/** Rendered width of a label line, estimated when canvas is unavailable. */
function labelTextWidth(text: string, size: number, weight: number): number {
  if (labelMeasureCtx === undefined) {
    try {
      labelMeasureCtx = document.createElement("canvas").getContext("2d");
    } catch {
      labelMeasureCtx = null;
    }
  }
  if (!labelMeasureCtx) return text.length * size * 0.6;
  const family = getComputedStyle(document.body).fontFamily || "sans-serif";
  labelMeasureCtx.font = `${weight} ${size}px ${family}`;
  return labelMeasureCtx.measureText(text).width;
}

/**
 * Names sit on the lower row with a short stem, leaving the top row free. A
 * name that does not fit before its left neighbor moves to the top row; its
 * stem runs from the top through the lower row, and its name may pass over
 * lower-row milestones because their stems start below it.
 */
function milestoneLabelLayout(labels: MilestoneLabelText[]): {
  sides: Array<"top" | "bottom">;
  widths: number[];
} {
  const sides: Array<"top" | "bottom"> = [];
  const widths: number[] = [];
  let lastTop: number | null = null;
  labels.forEach((label, index) => {
    const start = Math.max(0, label.x - MS_LABEL_MAX_W);
    const room = (prevX: number | null) =>
      Math.min(MS_LABEL_MAX_W, Math.max(0, label.x - (prevX ?? start) - 16));
    const need = Math.min(
      MS_LABEL_MAX_W,
      Math.ceil(
        Math.max(
          labelTextWidth(label.name, 13, 700),
          label.sub ? labelTextWidth(label.sub, 11, 400) + 16 : 0
        )
      )
    );
    const bottomW = room(index > 0 ? labels[index - 1].x : null);
    const topW = room(lastTop);
    if (bottomW >= need || bottomW >= topW) {
      sides.push("bottom");
      widths.push(bottomW);
    } else {
      sides.push("top");
      widths.push(topW);
      lastTop = label.x;
    }
  });
  return { sides, widths };
}
const REST_TOP = CHART_H;
const SNAP_R = 34;
const DRAG_START = 4;
/** Hover stays off this long after the last scroll event. */
const SCROLL_IDLE_MS = 150;
/** Holding a link pin inside this many pixels of either viewport edge scrolls. */
const LINK_SCROLL_EDGE = 28;
/** How far above the card's dot the edit button sits on a straight link. */
const LINK_COLOR = "var(--ciq-planner-accent)";
/** Opacity of links and bid cards outside the active milestone. */
const HIGHLIGHT_DIM = 0.35;
// High risk or late bid packages: card border, link and Required on site line.
const ALERT_COLOR = "var(--ciq-error-500)";
/** Their Required on site card border and the line to it (#FFCCC7). */
const ALERT_SITE_COLOR = "var(--ciq-red-200)";
const DEADLINE_COLOR = "var(--ciq-planner-deadline)";
/** Projected End and Today markers. */
const MARKER_INK = "var(--ciq-gray-960)";
/** End gutter after the last milestone when no Required on site column
 *  follows the track. */
const TRACK_END_GAP = 140;
const MIN_CARD_W = 220;
const UNPLANNED_CARD_GAP = 16;
const ROS_COL_W = 248;
/** Required on site, collapsed to its expand toggle. */
const ROS_COLLAPSED_W = 28;
const ROS_TOGGLE =
  "absolute inline-flex h-[22px] w-[22px] cursor-pointer items-center justify-center rounded border border-solid border-ciq-gray-300 bg-white p-0 text-ciq-planner-accent hover:bg-ciq-primary-bg";

type SpanPins = { firstX: number; lastX: number };

function planDayBounds(
  g: BidPackageRowGeometry
): { start: number; end: number } | null {
  const days = g.row.milestones
    .map((m) => m.effectiveDate)
    .filter((d): d is string => Boolean(d))
    .map(dayOf);
  if (!days.length) return null;
  return { start: Math.min(...days), end: Math.max(...days) };
}

/** Where a bid card sits. Dated cards are drawn from their status dates on the
 *  timeline scale (overflow zones included); a linked package with no date
 *  sits a short gap past the last milestone. */
type TimelineCard = {
  x: number;
  width: number;
  unplanned: boolean;
  /** The schedule starts in the left overflow zone / ends in the right one,
   *  so it runs on past what is drawn to scale. */
  continuesLeft?: boolean;
  continuesRight?: boolean;
};

/** A card drawn on the scale from day `start` to day `end`. It keeps
 *  MIN_CARD_W so its text fits; nothing is drawn inside it to mark a real end
 *  that falls short of its right edge (the Projected End marker does that
 *  above and below the card). */
function scaledCard(
  start: number,
  end: number,
  scale: PlannerTimeScale
): TimelineCard {
  const x = scale.x(start);
  const width = Math.max(scale.x(end) - x, MIN_CARD_W);
  return {
    x,
    width,
    unplanned: false,
    ...(scale.zoneOf(start) === "left" ? { continuesLeft: true } : {}),
    ...(scale.zoneOf(end) === "right" ? { continuesRight: true } : {})
  };
}

function timelineCard(
  g: BidPackageRowGeometry,
  pins: SpanPins | null,
  scale: PlannerTimeScale | null
): TimelineCard | null {
  // A package with no planned date sits past the last milestone (unplanned),
  // even when it has projected dates.
  const dated = bidHasMilestoneDate(g.row);
  if (scale && dated) {
    const bounds = scheduleDayBounds(g.row) ?? planDayBounds(g);
    if (bounds) return scaledCard(bounds.start, bounds.end, scale);
  } else if (!scale && g.bar && g.bar.width > 0) {
    // No dated design milestone, so no scale: keep the layout's box.
    return {
      x: g.bar.x,
      width: Math.max(g.bar.width, MIN_CARD_W),
      unplanned: false
    };
  }
  const milestoneX = g.linkBadge?.milestoneX;
  if (milestoneX == null || dated) return null;
  return {
    x: pins ? pins.lastX + UNPLANNED_CARD_GAP : milestoneX + UNPLANNED_CARD_GAP,
    width: MIN_CARD_W,
    unplanned: true
  };
}

function bidHasMilestoneDate(row: BidPackageRow): boolean {
  return row.milestones.some(
    (milestone) => milestone.plannedDate || milestone.effectiveDate
  );
}

/** No milestone dates, or a package the log marks as having no plan. */
function bidIsUnplanned(row: BidPackageRow): boolean {
  return row.state === "NO_PLAN" || !bidHasMilestoneDate(row);
}

function bidIsComplete(row: BidPackageRow): boolean {
  if (row.workflowStatus === "COMPLETED") return true;
  return (
    row.milestones.length > 0 &&
    row.milestones.every((milestone) => Boolean(milestone.actualDate))
  );
}

function bidWorkflow(row: BidPackageRow): BidWorkflowStatus {
  if (bidIsComplete(row)) return "COMPLETED";
  return row.workflowStatus ?? "NOT_STARTED";
}

type ScheduleKind = "Planned" | "Projected" | "Actual";
type ScheduleEnd = { kind: ScheduleKind; date: string } | null;
const DATE_FIELD: Record<
  ScheduleKind,
  "plannedDate" | "projectedDate" | "actualDate"
> = {
  Planned: "plannedDate",
  Projected: "projectedDate",
  Actual: "actualDate"
};

/** The first date the milestone has, in the given order of preference. */
function scheduleEnd(
  milestone: MilestoneNode | undefined,
  order: ScheduleKind[]
): ScheduleEnd {
  if (!milestone) return null;
  for (const kind of order) {
    const date = milestone[DATE_FIELD[kind]];
    if (date) return { kind, date };
  }
  return null;
}

/** Where the card sits on the scale and what its two date blocks say:
 *  not started — planned first and last milestone;
 *  started     — actual first milestone, projected last milestone;
 *  completed   — actual first and last milestone. */
function bidSchedule(row: BidPackageRow): {
  start: ScheduleEnd;
  end: ScheduleEnd;
} {
  const dated = row.milestones.filter(
    (m) => m.plannedDate || m.projectedDate || m.actualDate
  );
  const first = dated[0];
  const last = dated[dated.length - 1];
  const status = bidWorkflow(row);
  if (status === "COMPLETED") {
    return {
      start: scheduleEnd(first, ["Actual", "Projected", "Planned"]),
      end: scheduleEnd(last, ["Actual", "Projected", "Planned"])
    };
  }
  if (status === "IN_PROGRESS") {
    return {
      start: scheduleEnd(first, ["Actual", "Projected", "Planned"]),
      end: scheduleEnd(last, ["Projected", "Planned"])
    };
  }
  return {
    start: scheduleEnd(first, ["Planned", "Projected"]),
    end: scheduleEnd(last, ["Planned", "Projected"])
  };
}

/** The card's span in days, from bidSchedule. */
function scheduleDayBounds(
  row: BidPackageRow
): { start: number; end: number } | null {
  const { start, end } = bidSchedule(row);
  const startDate = start?.date ?? end?.date;
  const endDate = end?.date ?? start?.date;
  if (!startDate || !endDate) return null;
  const a = dayOf(startDate);
  const b = dayOf(endDate);
  return { start: Math.min(a, b), end: Math.max(a, b) };
}

function dayOf(iso: string): number {
  return dayOfIso(iso);
}

/** Projected Date of the last milestone — the "Projected End". None until
 *  a milestone has a planned date. */
function bidProjectedEnd(row: BidPackageRow): string | null {
  if (!bidHasMilestoneDate(row)) return null;
  const dated = row.milestones.filter(
    (m) => m.plannedDate || m.projectedDate || m.actualDate
  );
  return dated[dated.length - 1]?.projectedDate ?? null;
}

function bidCardKind(row: BidPackageRow): "planned" | "complete" | "unplanned" {
  if (bidIsComplete(row)) return "complete";
  if (bidHasMilestoneDate(row)) return "planned";
  return "unplanned";
}

/** High risk, or a negative float (the package is late). A completed package
 *  is no longer at risk. */
function bidIsAlert(row: BidPackageRow): boolean {
  if (bidIsComplete(row)) return false;
  return (
    row.riskLevel === "HIGH" ||
    (row.effectiveFloat != null && row.effectiveFloat < 0)
  );
}

function bidCardTone(row: BidPackageRow): {
  card: string;
  title: string;
  dates: string;
  strip: string;
  /** The card's border color. */
  edge: string;
} {
  const kind = bidCardKind(row);
  const dates = "text-xs font-normal leading-4 text-ciq-planner-ink2";
  const title =
    kind === "complete"
      ? "truncate text-xs font-semibold leading-4 text-ciq-planner-ink3"
      : kind === "unplanned"
        ? "truncate text-[11px] font-bold leading-4 text-ciq-planner-ink2"
        : "truncate text-xs font-semibold leading-4 text-ciq-black";
  if (bidIsAlert(row)) {
    const fill = kind === "unplanned" ? "bg-white" : "bg-ciq-warning-card";
    return {
      card: `border-[1.6px] border-solid border-ciq-error-500 ${fill}`,
      title,
      dates,
      strip: "bg-ciq-error-500",
      edge: ALERT_COLOR
    };
  }
  if (kind === "complete") {
    return {
      card: "border border-solid border-ciq-gray-300 bg-ciq-neutral-25",
      title,
      dates,
      strip: "bg-ciq-gray-300",
      edge: "var(--ciq-gray-300)"
    };
  }
  if (kind === "unplanned") {
    return {
      card: "border border-dashed border-ciq-planner-bid-open bg-white",
      title,
      dates,
      strip: "bg-ciq-planner-bid-open",
      edge: "var(--ciq-planner-bid-open)"
    };
  }
  return {
    card: "border border-dashed border-ciq-warning-text bg-ciq-warning-card",
    title,
    dates,
    strip: "bg-ciq-warning-text",
    edge: "var(--ciq-warning-text)"
  };
}

const START_LINE_W = 1;
const START_LINE_DASH: [number, number] = [5, 4];
/** Start lines are always #FAAD14, whatever the card's state. */
const START_LINE_COLOR = "var(--ciq-warning-text)";
/** Bottom of the date axis strip in the milestone chart. */
const AXIS_BOTTOM = MS_HEAD_H + 30;
/** Required on site's title and toggle: 7px into the Bid Packages header row. */
const ROS_HEADER_TOP = AXIS_BOTTOM + 7;

function LegendGroup(props: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`flex h-[26px] shrink-0 items-center gap-3 rounded-lg border border-solid border-ciq-gray-300 bg-white px-3 ${
        props.className ?? ""
      }`}
    >
      {props.children}
    </div>
  );
}

function LegendItem(props: { label: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      {props.children}
      {props.label}
    </span>
  );
}

function LegendSwitch(props: {
  testId: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap">
      {props.label}
      <Switch
        size="small"
        data-testid={props.testId}
        aria-label={props.label}
        checked={props.checked}
        onChange={props.onChange}
      />
    </label>
  );
}

/** Every link segment, stem and elbow alike, is this wide. A whole number,
 *  so centring a segment on its point (-translate-x/y-1/2) stays on the pixel
 *  grid and vertical and horizontal segments render at the same weight. */
const LINK_W = 2;

function bidLinkPaint(
  row: BidPackageRow,
  lit: boolean
): { color: string; dash: [number, number] | null; w: number } {
  // Highlighted: dashed, and still red for a high-risk or late package.
  if (lit) {
    return {
      color: bidIsAlert(row) ? ALERT_COLOR : LINK_COLOR,
      dash: [4, 4],
      w: LINK_W
    };
  }
  // High risk or late: a solid error-colour stroke all the way to the milestone.
  if (bidIsAlert(row)) return { color: ALERT_COLOR, dash: null, w: LINK_W };
  if (bidCardKind(row) === "planned") {
    return { color: "var(--ciq-planner-ink3)", dash: null, w: LINK_W };
  }
  return { color: "var(--ciq-planner-link-muted)", dash: null, w: LINK_W };
}

type Pt = [number, number];

type BarBox = { left: number; right: number; top: number; cy: number };

/** A link that has to go around its card turns this far left of the card. */
const LINK_DETOUR_DX = 12;
/** ...and runs this far above the card's top. */
const LINK_DETOUR_DY = 14;

/** Every link ends at the card's start (its left edge, vertically centred).
 *  A milestone before the card drops straight down and turns right into it;
 *  one over or after the card drops to just above it, runs left past its
 *  start, and comes down and back into it. The turn never leaves the track:
 *  a card at the very start of the timeline (the left overflow zone's
 *  furthest date) has less room, so the turn sits nearer it, and with no
 *  room at all the link comes down onto the card's start from above. */
function linkPoints(mx: number, bar: BarBox, top: number): Pt[] {
  if (mx < bar.left - 4) {
    return [
      [mx, top],
      [mx, bar.cy],
      [bar.left, bar.cy]
    ];
  }
  const y = bar.top - LINK_DETOUR_DY;
  const x = Math.max(bar.left - LINK_DETOUR_DX, LINK_W);
  if (x >= bar.left - LINK_W) {
    return [
      [mx, top],
      [mx, y],
      [bar.left, y],
      [bar.left, bar.cy]
    ];
  }
  return [
    [mx, top],
    [mx, y],
    [x, y],
    [x, bar.cy],
    [bar.left, bar.cy]
  ];
}

// The re-link ball always sits on the card's start (left edge), vertically
// centred, clear of the bid package name.
function cardHandlePoint(bar: BarBox): Pt {
  return [bar.left, bar.cy];
}

function DragCurve(props: {
  ax: number;
  ay: number;
  c1x: number;
  c1y: number;
  ex: number;
  ey: number;
  snapped: boolean;
}) {
  const { ax, ay, c1x, c1y, ex, ey, snapped } = props;
  const midY = (ay + ey) / 2;
  return (
    <svg
      data-testid="planner-bp-drag-curve"
      className="pointer-events-none fixed left-0 top-0 z-[2100]"
      width={window.innerWidth}
      height={window.innerHeight}
      overflow="visible"
    >
      <path
        data-testid="planner-bp-drag-path"
        d={`M ${ax} ${ay} C ${c1x} ${c1y} ${ex} ${midY} ${ex} ${ey}`}
        style={{
          fill: "none",
          stroke: LINK_COLOR,
          strokeWidth: 1.6,
          strokeDasharray: "4 4"
        }}
      />
      <circle
        cx={ax}
        cy={ay}
        r={7}
        style={{ fill: "white", stroke: LINK_COLOR, strokeWidth: 2.4 }}
      />
      <circle
        cx={ex}
        cy={ey}
        r={snapped ? 7 : 5}
        style={
          snapped
            ? { fill: "white", stroke: LINK_COLOR, strokeWidth: 2.4 }
            : { fill: LINK_COLOR, stroke: "white", strokeWidth: 2 }
        }
      />
    </svg>
  );
}

function dashes(
  color: string,
  dir: "bottom" | "right",
  on: number,
  gap: number
) {
  return `repeating-linear-gradient(to ${dir}, ${color} 0 ${on}px, transparent ${on}px ${on + gap}px)`;
}

function daysBefore(start: string, milestone: string) {
  return Math.round(
    (Date.parse(milestone.slice(0, 10)) - Date.parse(start.slice(0, 10))) /
      86_400_000
  );
}

function LinkChip(props: {
  kind: "dark" | "light" | "warn";
  x: number;
  y: number;
  title: string;
  detail?: string;
  /** Above (x, y): true ends the chip at x, "right" starts it at x,
   *  "center" centres it on x. */
  above?: boolean | "right" | "center";
  /** Draws a caret at the bottom, pointing at (x, y). Needs above="center". */
  caret?: boolean;
  testId?: string;
}) {
  const { kind, x, y, title, detail, above, caret, testId } = props;
  const box =
    kind === "dark"
      ? "bg-ciq-gray-975"
      : kind === "warn"
        ? "border border-solid border-ciq-red-400 bg-white"
        : "border border-solid border-ciq-gray-200 bg-white";
  const titleColor =
    kind === "dark"
      ? "text-white"
      : kind === "warn"
        ? "text-ciq-red-800"
        : "text-black";
  const detailColor =
    kind === "dark"
      ? "text-ciq-gray-400"
      : kind === "warn"
        ? "text-ciq-red-800"
        : "text-ciq-fg-muted";
  const caretColor =
    kind === "dark"
      ? "border-t-ciq-gray-975"
      : kind === "warn"
        ? "border-t-ciq-red-400"
        : "border-t-ciq-gray-200";
  return (
    <div
      data-testid={testId}
      className={`pointer-events-none absolute z-50 flex flex-col gap-0.5 whitespace-nowrap rounded-lg px-3 py-2 shadow-[0_6px_16px_rgba(0,0,0,0.18)] ${box} ${
        above === "right"
          ? "-translate-y-full"
          : above === "center"
            ? "-translate-x-1/2 -translate-y-full"
            : above
              ? "-translate-x-full -translate-y-full"
              : ""
      }`}
      style={{ left: x, top: y }}
    >
      <span className={`text-[13px] font-medium leading-[17px] ${titleColor}`}>
        {title}
      </span>
      {detail ? (
        <span className={`text-xs leading-4 ${detailColor}`}>{detail}</span>
      ) : null}
      {caret ? (
        <span
          aria-hidden
          className={`absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-x-[5px] border-b-0 border-t-[6px] border-solid border-x-transparent ${caretColor}`}
        />
      ) : null}
    </div>
  );
}

function contentWidthOf(geometry: Geometry, config: RenderConfig) {
  return Math.max(
    1,
    geometry.width - config.labelColumnWidth - config.clusterWidth
  );
}

/** Float and delay are shown in whole days, rounded the same way everywhere. */
function wholeDays(days: number): number {
  return Math.round(days) || 0;
}

/** Formats the calendar date the timeline places the value on (its first ten
 *  characters, as utcDay reads it), so a label never lands a day away from its
 *  line in another time zone. */
function fmtCommitted(date: string | null) {
  return date
    ? DateUtils.format(date.slice(0, 10), {
        in: DATE_FORMAT_YYYYMMDD,
        out: DATE_FORMAT_MMDDYYYY
      })
    : "";
}

function fmtAxis(date: string) {
  return dayjs(date.slice(0, 10)).format("MMM D");
}

/** "Aug 15, 2026": the edge of the to-scale region, as an overflow zone and
 *  the date cursor name it. */
function fmtZoneEdge(day: number) {
  return dayjs(isoOfDay(day)).format("MMM D, YYYY");
}

// "MMM D" is about 48px at 10.5px. Ticks stay on the calendar cadence; the
// date is omitted when the previous label would sit on top of it.
const AXIS_LABEL_GAP = 48;
/** Half of what "15" paints. */
const AXIS_LABEL_MID_HALF = 11;
/** Half of what the "Today MM-DD-YYYY" pill paints. */
const TODAY_PILL_HALF = 52;

/** The Today pill is drawn on the axis row, over the labels. A label it would
 *  cover is dropped rather than hidden under it, which reads as a month
 *  missing from the scale. */
function todayClearance(
  todayX: number | null
): (x: number, half: number) => boolean {
  if (todayX == null) return () => true;
  return (x, half) =>
    x + half <= todayX - TODAY_PILL_HALF ||
    x - half >= todayX + TODAY_PILL_HALF;
}

/** A half-month tick's label and half the room it needs. The 15th is labelled
 *  just "15": "MMM D" wants the full AXIS_LABEL_GAP, and at most zooms the 1st
 *  and the 15th are closer together than that, which would drop every
 *  mid-month date. Under its own month "15" is unambiguous. */
function halfMonthLabel(day: number): { label: string; half: number } {
  const iso = isoOfDay(day);
  if (iso.endsWith("-15")) {
    return { label: "15", half: AXIS_LABEL_MID_HALF };
  }
  // January carries the year: "MMM D" alone would leave a scale running over
  // several years with no year on it anywhere.
  const label = iso.endsWith("-01-01")
    ? dayjs(iso).format("MMM 'YY")
    : fmtAxis(iso);
  return { label, half: AXIS_LABEL_GAP / 2 };
}

/** Half-month ticks need only half a "MMM D" plus half a "15" between them. */
const AXIS_LABEL_MID_PAIR_GAP = AXIS_LABEL_GAP / 2 + AXIS_LABEL_MID_HALF;

/** Room after the right overflow zone for the Deadline label and float chip. */
const TIMELINE_END_PAD = 96;

/** Every date a bid package row draws: its card ends, and — unless it is
 *  complete — its Projected End and Deadline. These decide whether the scale
 *  needs an overflow zone. */
function drawnBidDays(rows: BidPackageRowGeometry[]): number[] {
  const days: number[] = [];
  rows.forEach((g) => {
    // An unplanned card is parked past the last milestone, not on its dates.
    const bounds = bidHasMilestoneDate(g.row)
      ? (scheduleDayBounds(g.row) ?? planDayBounds(g))
      : null;
    if (bounds) days.push(bounds.start, bounds.end);
    if (bidWorkflow(g.row) === "COMPLETED") return;
    if (g.row.deadlineDate) days.push(dayOf(g.row.deadlineDate));
    const projected = bidProjectedEnd(g.row);
    if (projected) days.push(dayOf(projected));
  });
  return days;
}

/** The last day the axis has room for. A right overflow zone closes the axis
 *  at the to-scale edge — past it is "After", not a date. With no zone the
 *  axis strip runs the whole drawn width (the end pad, Required on site), and
 *  the ticks run with it rather than stopping at the last drawn date. */
function axisEndDay(scale: PlannerTimeScale, endX: number): number {
  if (scale.right) return scale.linearEndDay;
  return scale.linearEndDay + (endX - scale.linearEndX) / scale.pxPerDay;
}

/** Axis ticks on the 1st and the 15th of each month, across the to-scale
 *  region; the overflow zones carry their own label. */
function axisMarks(
  scale: PlannerTimeScale | null,
  fullTimeline = false,
  endX = 0,
  todayX: number | null = null
): Array<{ x: number; label: string }> {
  if (!scale) return [];
  const clearOfToday = todayClearance(todayX);
  if (fullTimeline) return fullTimelineAxisMarks(scale, endX, clearOfToday);
  const marks: Array<{ x: number; label: string }> = [];
  // Labels are kept from touching by their own widths: a narrow "15" must not
  // push the month label that follows it 14 days later off the axis.
  let lastLabelRight = Number.NEGATIVE_INFINITY;
  halfMonthTickDays(scale.linearStartDay, axisEndDay(scale, endX)).forEach(
    (day) => {
      const x = scale.x(day);
      // Keep a label clear of the break marker at a zone edge.
      const nearZone =
        (scale.left && x - scale.linearStartX < AXIS_LABEL_GAP / 2) ||
        (scale.right && scale.linearEndX - x < AXIS_LABEL_GAP / 2);
      const tick = halfMonthLabel(day);
      const showLabel =
        !nearZone &&
        x - tick.half >= lastLabelRight &&
        clearOfToday(x, tick.half);
      if (showLabel) lastLabelRight = x + tick.half;
      marks.push({ x, label: showLabel ? tick.label : "" });
    }
  );
  return marks;
}

/** Full-timeline ticks: the 1st and 15th, then months, quarters or years as
 *  the range gets longer and px per day smaller, with labels never closer
 *  than AXIS_LABEL_GAP. */
function fullTimelineAxisMarks(
  scale: PlannerTimeScale,
  endX: number,
  clearOfToday: (x: number, half: number) => boolean
): Array<{ x: number; label: string }> {
  const { unit, days } = fullTimelineTicks({
    startDay: scale.linearStartDay,
    endDay: axisEndDay(scale, endX),
    pxPerDay: scale.pxPerDay,
    minGapPx: AXIS_LABEL_GAP,
    minMidGapPx: AXIS_LABEL_MID_PAIR_GAP
  });
  // "Jun '27" is about as wide as "MMM D", so it fits the same label gap.
  const format = unit === "year" ? "YYYY" : "MMM 'YY";
  const marks: Array<{ x: number; label: string }> = [];
  let lastLabelRight = Number.NEGATIVE_INFINITY;
  days.forEach((day) => {
    const x = scale.x(day);
    const tick =
      unit === "halfMonth"
        ? halfMonthLabel(day)
        : {
            label: dayjs(isoOfDay(day)).format(format),
            half: AXIS_LABEL_GAP / 2
          };
    // Keep a label clear of the break marker at the left zone edge.
    const nearZone =
      scale.left != null && x - scale.linearStartX < AXIS_LABEL_GAP / 2;
    const showLabel =
      !nearZone &&
      x - tick.half >= lastLabelRight &&
      clearOfToday(x, tick.half);
    if (showLabel) lastLabelRight = x + tick.half;
    marks.push({ x, label: showLabel ? tick.label : "" });
  });
  return marks;
}

function pickerValue(date: string | null) {
  return date ? DateUtils.dateTimeObj(date) : null;
}

function MilestoneHoverCard(props: {
  milestone: MilestoneNode;
  milestones: MilestoneNode[];
  linkedCount: number;
  scopeCount: number;
  onRename?: (name: string) => void;
  onDelete?: () => void;
  canDelete?: boolean;
  onCommittedDate?: (isoDate: string) => void;
  onActualDate?: (isoDate: string | null) => void;
  onOpenScope?: () => void;
  onLockChange: (locked: boolean) => void;
}) {
  const {
    milestone,
    milestones,
    linkedCount,
    scopeCount,
    onRename,
    onDelete,
    canDelete = true,
    onCommittedDate,
    onActualDate,
    onOpenScope,
    onLockChange
  } = props;
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(milestone.name);
  const [picking, setPicking] = useState<"committed" | "actual" | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const lock = (editing: boolean, next: "committed" | "actual" | null) => {
    onLockChange(editing || next != null);
  };

  const commitName = () => {
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== milestone.name) onRename?.(trimmed);
    setEditingName(false);
    lock(false, picking);
  };

  const cancelName = () => {
    setNameDraft(milestone.name);
    setEditingName(false);
    lock(false, picking);
  };

  useEffect(() => {
    if (!editingName) return undefined;
    nameInputRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      cancelName();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // cancelName reads the current name and picker lock from this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingName]);

  const dateCell = (
    label: string,
    date: string | null,
    kind: "committed" | "actual",
    onSave?: (isoDate: string | null) => void,
    rules?: {
      disabled?: boolean;
      disabledDate?: (current: Dayjs) => boolean;
    }
  ) => (
    <div className="min-w-0">
      <div className="text-[10px] leading-3 text-ciq-gray-700">{label}</div>
      {picking === kind ? (
        <CIQDatePicker
          open
          allowClear={false}
          format={DATE_FORMAT_MMDDYYYY}
          value={pickerValue(date)}
          className="mt-1 w-full"
          getPopupContainer={() => document.body}
          disabledDate={rules?.disabledDate}
          renderExtraFooter={() =>
            kind === "actual" && date ? (
              <div className="flex justify-center py-2">
                <Button
                  data-testid="planner-ms-clear-actual-date"
                  onClick={() => {
                    onSave?.(null);
                    setPicking(null);
                    lock(editingName, null);
                  }}
                >
                  Clear Date
                </Button>
              </div>
            ) : null
          }
          onOpenChange={(open) => {
            if (!open) {
              setPicking(null);
              lock(editingName, null);
            }
          }}
          onChange={(d) => {
            onSave?.(d ? DateUtils.formatDateWithLunchTime(d) : null);
            setPicking(null);
            lock(editingName, null);
          }}
        />
      ) : (
        <button
          type="button"
          data-testid={`planner-ms-${kind}-date`}
          disabled={!onSave || rules?.disabled}
          className="mt-0.5 flex items-center gap-1 border-none bg-transparent p-0 text-left text-xs font-semibold text-black disabled:cursor-not-allowed disabled:text-ciq-gray-400"
          onClick={() => {
            if (!onSave || rules?.disabled) return;
            setPicking(kind);
            lock(editingName, kind);
          }}
        >
          {date ? (
            fmtCommitted(date)
          ) : (
            <span
              className={`font-medium ${
                rules?.disabled ? "text-ciq-gray-400" : "text-ciq-primary-900"
              }`}
            >
              + Add
            </span>
          )}
          {date && onSave && !rules?.disabled ? (
            <EditOutlined className="text-[11px] text-ciq-planner-accent" />
          ) : null}
        </button>
      )}
    </div>
  );

  const actualEditable = isActualDateEditable(milestones, milestone.id);
  // Hidden, not disabled, while the actual date can't be added.
  const addActualDate =
    !milestone.actualDate &&
    picking !== "committed" &&
    onActualDate &&
    actualEditable ? (
      picking === "actual" ? (
        <CIQDatePicker
          open
          allowClear
          format={DATE_FORMAT_MMDDYYYY}
          value={pickerValue(milestone.actualDate)}
          className="mt-2 w-full"
          getPopupContainer={() => document.body}
          disabledDate={(current) =>
            isActualDateDisabled(current, milestones, milestone.id)
          }
          onOpenChange={(open) => {
            if (!open) {
              setPicking(null);
              lock(editingName, null);
            }
          }}
          onChange={(d) => {
            onActualDate?.(d ? DateUtils.formatDateWithLunchTime(d) : null);
            setPicking(null);
            lock(editingName, null);
          }}
        />
      ) : (
        <button
          type="button"
          data-testid="planner-ms-actual-date"
          className="mt-2 flex w-full items-center gap-1 border-none bg-transparent p-0 text-left text-[11px] font-medium text-ciq-planner-accent"
          onClick={() => {
            setPicking("actual");
            lock(editingName, "actual");
          }}
        >
          <CalendarOutlined className="text-[11px]" />
          Add Actual Date
        </button>
      )
    ) : null;

  return (
    <div className="w-[280px] rounded-lg border border-solid border-ciq-gray-100 bg-white p-3 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-ciq-primary-900" />
        {editingName ? (
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <input
              ref={nameInputRef}
              data-testid="planner-ms-name-input"
              className="min-w-0 flex-1 rounded border border-solid border-ciq-border-subtle px-2 py-0.5 text-sm font-semibold"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitName();
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  cancelName();
                }
              }}
            />
            <button
              type="button"
              aria-label="Save milestone name"
              data-testid="planner-ms-name-save"
              className="border-none bg-transparent p-0 text-ciq-primary-900"
              onClick={commitName}
            >
              <CheckOutlined />
            </button>
          </div>
        ) : (
          <>
            <div className="min-w-0 flex-1 truncate text-sm font-semibold text-black">
              {milestone.name}
            </div>
            {onRename ? (
              <button
                type="button"
                aria-label="Edit milestone name"
                data-testid="planner-ms-name-edit"
                className="border-none bg-transparent p-0 text-ciq-planner-accent"
                onClick={() => {
                  setNameDraft(milestone.name);
                  setEditingName(true);
                  lock(true, picking);
                }}
              >
                <EditOutlined />
              </button>
            ) : null}
            {onDelete ? (
              <button
                type="button"
                aria-label="Delete milestone"
                data-testid="planner-ms-delete"
                disabled={!canDelete}
                className="border-none bg-transparent p-0 text-ciq-error-500 disabled:cursor-not-allowed disabled:text-ciq-gray-300"
                title={
                  canDelete
                    ? undefined
                    : "A design package needs at least two milestones."
                }
                onClick={() => {
                  if (!canDelete) return;
                  onDelete();
                }}
              >
                <DeleteOutlined />
              </button>
            ) : null}
          </>
        )}
      </div>
      <div className="mt-3 rounded bg-ciq-neutral-25 px-2 py-1.5">
        <div className="grid grid-cols-2 items-start gap-2">
          {dateCell(
            "Committed Date",
            milestone.committedDate,
            "committed",
            onCommittedDate
              ? (iso) => {
                  if (iso) onCommittedDate(iso);
                }
              : undefined,
            {
              // An actual date locks the milestone's committed date.
              disabled: Boolean(milestone.actualDate),
              disabledDate: (current) =>
                isCommittedDateDisabled(current, milestones, milestone.id)
            }
          )}
          {milestone.actualDate ? (
            dateCell(
              "Actual Date",
              milestone.actualDate,
              "actual",
              onActualDate,
              {
                disabled: !isActualDateEditable(milestones, milestone.id),
                disabledDate: (current) =>
                  isActualDateDisabled(current, milestones, milestone.id)
              }
            )
          ) : (
            <div className="min-w-0">
              <div className="text-[10px] leading-3 text-ciq-gray-700">
                Projected Date
              </div>
              <div className="mt-0.5 text-xs font-semibold text-black">
                {milestone.projectedDate
                  ? fmtCommitted(milestone.projectedDate)
                  : "—"}
              </div>
            </div>
          )}
        </div>
        {addActualDate}
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-xs text-ciq-fg-muted">
        <LinkOutlined className="text-ciq-black-a88 hover:text-ciq-black-a98" />
        Linked to {linkedCount} Bid Package{linkedCount === 1 ? "" : "s"}
      </div>
      <button
        type="button"
        data-testid="planner-ms-scope"
        className="mt-3 inline-flex w-fit cursor-pointer items-center gap-1.5 self-start whitespace-nowrap rounded-md border border-solid border-ciq-gray-300 bg-white px-3 py-1.5 text-[13px] font-medium leading-4 text-ciq-planner-accent hover:bg-ciq-primary-bg disabled:cursor-default disabled:text-ciq-gray-400"
        onClick={onOpenScope}
        disabled={!onOpenScope}
      >
        {scopeCount} Scope Item{scopeCount === 1 ? "" : "s"}
        <RightOutlined className="text-[11px] text-ciq-planner-accent" />
      </button>
    </div>
  );
}

/** A card edge in an overflow zone: the schedule runs on past what is drawn
 *  to scale. A faded edge with an arrow pointing the way it continues. */
function CardContinues(props: {
  side: "left" | "right";
  id: string;
  color: string;
}) {
  const { side, id, color } = props;
  const left = side === "left";
  return (
    <span
      data-testid={`planner-bp-continues-${side}-${id}`}
      title={`${left ? "Starts" : "Ends"} in an area not shown to scale`}
      className={`absolute inset-y-0 flex w-3 items-center ${
        left ? "left-0 justify-start pl-px" : "right-0 justify-end pr-px"
      }`}
      style={{
        background: `linear-gradient(to ${left ? "right" : "left"}, color-mix(in srgb, ${color} 35%, transparent), transparent)`
      }}
    >
      <span aria-hidden className="text-[7px] leading-none" style={{ color }}>
        {left ? "◀" : "▶"}
      </span>
    </span>
  );
}

/** Diagonal hatching for an overflow zone: dates there are not to scale. */
const ZONE_HATCH =
  "repeating-linear-gradient(135deg, var(--ciq-gray-100) 0 4px, transparent 4px 9px)";
const ZONE_TITLE = "Dates in this area are not shown to scale";

/** The ⫽ axis-break marker where a zone meets the to-scale region. */
function AxisBreak(props: { x: number; top: number; testId: string }) {
  return (
    <span
      data-testid={props.testId}
      aria-hidden
      className="pointer-events-none absolute z-[14] flex h-4 w-3 -translate-x-1/2 items-center justify-center gap-[3px] bg-ciq-planner-axis"
      style={{ left: props.x, top: props.top }}
    >
      <span className="h-3.5 w-px rotate-[20deg] bg-ciq-gray-500" />
      <span className="h-3.5 w-px rotate-[20deg] bg-ciq-gray-500" />
    </span>
  );
}

function ScheduleBlock(props: {
  testId: string;
  end: ScheduleEnd;
  side: "Start" | "End";
  /** The end date is before the start date. */
  inverted?: boolean;
}) {
  const { testId, end, side, inverted } = props;
  if (!end) return <span />;
  return (
    <span
      data-testid={testId}
      data-inverted={inverted || undefined}
      title={inverted ? "End date is before the start date" : undefined}
      className={`flex min-w-0 flex-col ${side === "End" ? "items-end text-right" : ""}`}
    >
      <span className="truncate text-[10px] font-normal leading-[13px] text-ciq-black-a65">
        {end.kind} {side}
      </span>
      <span
        className={`truncate text-[10px] font-semibold leading-[13px] ${
          inverted ? "text-ciq-red-600" : "text-black"
        }`}
      >
        {fmtCommitted(end.date)}
      </span>
    </span>
  );
}

/** A card with no schedule to show: unplanned, or neither date set. */
function bidCardHasNoPlan(row: BidPackageRow, unplanned: boolean): boolean {
  const { start, end } = bidSchedule(row);
  return unplanned || (!start && !end);
}

/** Title, then Start on the left and End on the right. Shared by the card on
 *  the timeline and its copy in the hover card. */
function BidCardBody(props: {
  row: BidPackageRow;
  unplanned: boolean;
  /** Opens the Bid Package details page from its No Plan line. */
  onCreatePlan?: () => void;
}) {
  const { row, unplanned, onCreatePlan } = props;
  const title = bidPackageLabel(row.number, row.name);
  const { start, end } = bidSchedule(row);
  // The card is drawn from the earlier date to the later one either way; the
  // End date turns red so the bad data is not hidden.
  const inverted = Boolean(start && end && dayOf(end.date) < dayOf(start.date));
  const noPlan = bidCardHasNoPlan(row, unplanned);
  // Create Plan sits on the right, centred on the whole card, so the title
  // stops short of it.
  const createPlan = noPlan && onCreatePlan ? onCreatePlan : undefined;
  return (
    <>
      <div
        className={`flex min-w-0 items-center justify-between gap-1 ${
          createPlan ? "pr-16" : ""
        }`}
      >
        <span
          className="truncate text-[10.4px] font-bold leading-[14px] text-ciq-black-a88"
          title={title}
        >
          {title}
        </span>
      </div>
      {noPlan ? (
        <div className="flex min-w-0 items-center whitespace-nowrap text-[10px] leading-[13px]">
          <span
            data-testid={`planner-bp-no-plan-${row.id}`}
            className="truncate text-ciq-black-a65"
          >
            No Plan
          </span>
          {createPlan ? (
            <button
              type="button"
              data-testid={`planner-bp-link-plan-${row.id}`}
              aria-label={`Create plan for ${title}`}
              className="absolute right-[9px] top-1/2 -translate-y-1/2 cursor-pointer whitespace-nowrap border-0 bg-transparent p-0 text-[10px] leading-[13px] text-ciq-planner-accent hover:underline"
              onClick={(event) => {
                event.stopPropagation();
                createPlan();
              }}
              onKeyDown={(event) => event.stopPropagation()}
            >
              Create Plan
            </button>
          ) : null}
        </div>
      ) : (
        <div className="flex min-w-0 items-end justify-between gap-2">
          <ScheduleBlock
            testId={`planner-bp-start-${row.id}`}
            end={start}
            side="Start"
          />
          <ScheduleBlock
            testId={`planner-bp-end-${row.id}`}
            end={end}
            side="End"
            inverted={inverted}
          />
        </div>
      )}
    </>
  );
}

/** The design package's delay chip: its current_delay from the API (never
 *  worked out from dates here), drawn once, beside the duration into the next
 *  design milestone (the first one without an Actual Date). None when the
 *  delay is missing, zero or negative. */
function nextMilestoneDelay(
  milestones: MilestoneNode[],
  currentDelay: number | null | undefined
): { milestoneId: string; days: number } | null {
  const days = currentDelay == null ? 0 : wholeDays(currentDelay);
  if (days <= 0) return null;
  const next = milestones.find((m) => !m.actualDate);
  return next ? { milestoneId: next.id, days } : null;
}

function MilestoneDelayChip(props: { days: number }) {
  const { days } = props;
  return (
    <span
      data-testid="planner-ms-delay"
      title={`Projected ${days} ${days === 1 ? "day" : "days"} late`}
      className="block h-5 whitespace-nowrap rounded-[5px] border border-solid border-ciq-red-400 bg-ciq-red-50 px-1.5 text-xs leading-[18px] text-ciq-red-800"
    >
      +{days}d delay
    </span>
  );
}

/** A small filled triangle; `down` points at the line below it. */
function MarkerArrow(props: {
  x: number;
  y: number;
  color: string;
  dir: "down" | "up";
  fade?: CSSProperties;
}) {
  const { x, y, color, dir, fade } = props;
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute h-0 w-0 -translate-x-1/2 border-x-[5px] border-solid border-x-transparent"
      style={{
        ...fade,
        left: x,
        top: dir === "down" ? y : y - 6,
        ...(dir === "down"
          ? { borderTopWidth: 6, borderTopColor: color, borderBottomWidth: 0 }
          : {
              borderBottomWidth: 6,
              borderBottomColor: color,
              borderTopWidth: 0
            })
      }}
    />
  );
}

function floatText(days: number): string {
  const value = wholeDays(days);
  return `${value > 0 ? "+" : ""}${value}d float`;
}

/** Space between a marker line and its label. */
const MARKER_LABEL_GAP = 6;
const MARKER_LABEL_SIZE = 11;
const PROJECTED_LABEL_SIZE = 10;
/** 65% black, softer than the Projected End line itself. */
const PROJECTED_LABEL_INK = "var(--ciq-black-a65)";

/** Width of a marker label: its title or its date, whichever is longer. */
function markerLabelWidth(
  title: string,
  date: string,
  size = MARKER_LABEL_SIZE,
  dateWeight = 400
): number {
  return Math.ceil(
    Math.max(
      labelTextWidth(title, size, 400),
      labelTextWidth(fmtCommitted(date), size, dateWeight)
    )
  );
}

/** A marker's title and date under the card, beside its line: to the right
 *  of it, or to the left when the other marker's label would cover it. */
function MarkerLabel(props: {
  testId: string;
  x: number;
  side: "left" | "right";
  color: string;
  title: string;
  date: string;
  fade: CSSProperties;
  /** 10px type with a semibold black date, for the Projected End label. */
  small?: boolean;
}) {
  const { testId, x, side, color, title, date, fade, small } = props;
  return (
    <span
      data-testid={testId}
      data-side={side}
      className={`pointer-events-none absolute z-[6] flex flex-col whitespace-nowrap leading-[13px] ${
        small ? "text-[10px]" : "text-[11px]"
      } ${side === "left" ? "-translate-x-full items-end text-right" : ""}`}
      style={{
        ...fade,
        color,
        left: side === "left" ? x - MARKER_LABEL_GAP : x + MARKER_LABEL_GAP,
        bottom: BID_ROW_H - MARKER_BOTTOM
      }}
    >
      <span>{title}</span>
      <span className={small ? "font-semibold text-black" : undefined}>
        {fmtCommitted(date)}
      </span>
    </span>
  );
}

/** Deadline, Projected End and the Actual Float between them, drawn in the
 *  bid package's own row. Completed bid packages show none of them. With
 *  neither a Deadline nor a Projected End, the float sits beside the card. */
function BidRowMarkers(props: {
  row: BidPackageRow;
  scale: PlannerTimeScale;
  cardRight: number;
  /** Faded with its card while another milestone is highlighted. */
  faded?: boolean;
}) {
  const { row, scale, cardRight, faded = false } = props;
  if (bidWorkflow(row) === "COMPLETED") return null;
  // Each marker takes the fade itself: a faded wrapper would be its own
  // stacking context and drop the markers under the card.
  const fade: CSSProperties = {
    opacity: faded ? HIGHLIGHT_DIM : 1,
    transition: "opacity 150ms"
  };
  const deadline = row.deadlineDate ?? null;
  const projected = bidProjectedEnd(row);
  const dx = deadline ? scale.x(dayOf(deadline)) : null;
  const px = projected ? scale.x(dayOf(projected)) : null;
  const float = row.effectiveFloat;

  let bracket: { left: number; right: number } | null = null;
  if (dx != null && px != null && Math.abs(dx - px) >= 12) {
    bracket = { left: Math.min(dx, px), right: Math.max(dx, px) };
  }
  const chipX =
    dx != null || px != null
      ? Math.max(dx ?? -Infinity, px ?? -Infinity)
      : cardRight;
  // Both labels sit right of their line. When the left marker's label would
  // reach the other line, it moves to the left of its own line instead.
  let deadlineSide: "left" | "right" = "right";
  let projectedSide: "left" | "right" = "right";
  if (dx != null && px != null && deadline && projected) {
    const projectedFirst = px <= dx;
    const firstW = projectedFirst
      ? markerLabelWidth("Projected End", projected, PROJECTED_LABEL_SIZE, 600)
      : markerLabelWidth("Deadline", deadline);
    if (Math.abs(dx - px) < firstW + MARKER_LABEL_GAP * 2) {
      if (projectedFirst) projectedSide = "left";
      else deadlineSide = "left";
    }
  }

  return (
    <>
      {dx != null && deadline ? (
        <>
          <div
            data-testid={`planner-bp-deadline-${row.id}`}
            aria-hidden
            className="pointer-events-none absolute z-[5] -translate-x-1/2"
            style={{
              ...fade,
              left: dx,
              top: MARKER_TOP,
              width: 2,
              height: MARKER_H,
              background: DEADLINE_COLOR
            }}
          />
          <MarkerArrow
            x={dx}
            y={MARKER_TOP}
            color={DEADLINE_COLOR}
            dir="down"
            fade={fade}
          />
          <MarkerArrow
            x={dx}
            y={MARKER_BOTTOM}
            color={DEADLINE_COLOR}
            dir="up"
            fade={fade}
          />
          <MarkerLabel
            testId={`planner-bp-deadline-label-${row.id}`}
            x={dx}
            side={deadlineSide}
            color={DEADLINE_COLOR}
            title="Deadline"
            date={deadline}
            fade={fade}
          />
        </>
      ) : null}
      {px != null && projected ? (
        <>
          <div
            data-testid={`planner-bp-projected-${row.id}`}
            aria-hidden
            // Under the card (z-10): the line shows above and below it only.
            className="pointer-events-none absolute z-[9] -translate-x-1/2"
            style={{
              ...fade,
              left: px,
              top: MARKER_TOP,
              width: 1.6,
              // As tall as the Deadline line. The card can be wider than its
              // dates (MIN_CARD_W), so the marker always runs past it.
              height: MARKER_H,
              background: dashes(MARKER_INK, "bottom", 2, 2)
            }}
          />
          <MarkerArrow
            x={px}
            y={MARKER_TOP}
            color={MARKER_INK}
            dir="down"
            fade={fade}
          />
          <MarkerArrow
            x={px}
            y={MARKER_BOTTOM}
            color={MARKER_INK}
            dir="up"
            fade={fade}
          />
          <MarkerLabel
            testId={`planner-bp-projected-label-${row.id}`}
            x={px}
            side={projectedSide}
            color={PROJECTED_LABEL_INK}
            title="Projected End"
            date={projected}
            fade={fade}
            small
          />
        </>
      ) : null}
      {bracket ? (
        <div
          data-testid={`planner-bp-float-bracket-${row.id}`}
          aria-hidden
          className="pointer-events-none absolute z-[6]"
          style={{
            ...fade,
            left: bracket.left + 3,
            top: FLOAT_Y,
            width: bracket.right - bracket.left - 6
          }}
        >
          <span className="absolute inset-x-0 top-0 h-px bg-ciq-gray-500" />
          <span className="absolute -top-[3px] left-0 h-[7px] w-[7px] rotate-45 border-0 border-b border-l border-solid border-ciq-gray-500" />
          <span className="absolute -top-[3px] right-0 h-[7px] w-[7px] -rotate-[135deg] border-0 border-b border-l border-solid border-ciq-gray-500" />
        </div>
      ) : null}
      {float != null ? (
        <span
          data-testid={`planner-bp-float-${row.id}`}
          className={`pointer-events-none absolute z-[6] -translate-y-1/2 whitespace-nowrap rounded border border-solid px-1.5 text-[11px] leading-4 ${
            float < 0
              ? "border-ciq-red-700 bg-ciq-red-50 text-ciq-error-500"
              : "border-ciq-gray-300 bg-ciq-gray-25 text-ciq-planner-ink3"
          }`}
          style={{ ...fade, left: chipX + 8, top: FLOAT_Y }}
        >
          {floatText(float)}
        </span>
      ) : null}
    </>
  );
}

function hiddenIdsAfterToggleAll(
  current: Set<string>,
  ids: string[]
): Set<string> {
  const allShown = ids.every((id) => !current.has(id));
  const next = new Set(current);
  ids.forEach((id) => {
    if (allShown) next.add(id);
    else next.delete(id);
  });
  return next;
}

function highlightedBidIds(input: {
  dragActive: boolean;
  /** Milestone picked from its link count; outranks any hover. */
  activeMilestoneId: string | null;
  linkHoverId: string | null;
  hoveredMilestoneId: string | null;
  linkLayout: { milestoneId: string; g: { row: { id: string } } }[];
}): Set<string> {
  const litIds = new Set<string>();
  if (input.dragActive) return litIds;
  const milestoneId = input.activeMilestoneId ?? null;
  if (!milestoneId && input.linkHoverId) {
    litIds.add(input.linkHoverId);
    return litIds;
  }
  const lit = milestoneId ?? input.hoveredMilestoneId;
  if (!lit) return litIds;
  input.linkLayout.forEach((link) => {
    if (link.milestoneId === lit) litIds.add(link.g.row.id);
  });
  return litIds;
}

/** The one date <-> x mapping for everything a lane draws: the design span to
 *  scale with lead and trail margins, plus compressed overflow zones for bid
 *  package dates (and Today) beyond them. The layout's own scale gives the
 *  design span and its px per day. */
export function plannerTimeScaleOf(
  layout: Geometry,
  todayIso: string,
  /** Full timeline: no zones. The zones, margins and (full timeline) ends fit
   *  `visibleRows` (the filtered and searched bid packages), so they are
   *  recomputed on every filter. */
  options?: { fullTimeline?: boolean; visibleRows?: BidPackageRowGeometry[] }
): PlannerTimeScale | null {
  const { start, end } = layout.inScope;
  const firstIso = layout.xToDate(start);
  const lastIso = layout.xToDate(end);
  if (!firstIso || !lastIso) return null;
  const firstDay = dayOf(firstIso);
  const lastDay = dayOf(lastIso);
  const fullTimeline = Boolean(options?.fullTimeline);
  const today = dayOf(todayIso);
  return buildPlannerTimeScale({
    milestoneDays: [firstDay, lastDay],
    pxPerDay: lastDay > firstDay ? (end - start) / (lastDay - firstDay) : 0,
    itemDays: [...drawnBidDays(options?.visibleRows ?? layout.rows), today],
    emptyLeadPx: TIMELINE_EMPTY_LEAD_PX,
    emptyTrailPx: TIMELINE_EMPTY_TRAIL_PX,
    fullTimeline
  });
}

/** Is a bid package row left in the list by the search and the filter? */
function bidRowShown(
  g: BidPackageRowGeometry,
  q: string,
  hiddenBidIds: Set<string>
): boolean {
  if (hiddenBidIds.has(g.row.id)) return false;
  if (!q) return true;
  return `${g.row.number} ${g.row.name}`.toLowerCase().includes(q);
}

export default function PlannerLaneBody(props: {
  geometry: Geometry;
  config: RenderConfig;
  onAddMilestone?: (placement: AddMilestonePlacement) => void;
  onMilestoneReschedule?: (milestoneId: string, isoDate: string) => void;
  onMilestoneActualDate?: (milestoneId: string, isoDate: string | null) => void;
  onRenameMilestone?: (milestoneId: string, nextName: string) => void;
  onDeleteMilestone?: (
    milestoneId: string,
    options?: DeleteMilestoneOptions
  ) => void | Promise<void>;
  milestoneScopeItems?: Record<string, string[]>;
  onMilestoneScopeOpen?: (m: { milestoneId: string; label: string }) => void;
  bidScopeItems?: Record<string, string[]>;
  onBidScopeOpen?: (b: {
    bidPackageId: string;
    label: string;
    milestoneName?: string;
  }) => void;
  onLinkChange?: (
    bpId: string,
    milestoneId: string,
    info?: { caution?: string }
  ) => void;
  /** When omitted, every dated milestone is a valid drop target. */
  canLinkBidToMilestone?: (bpId: string, milestoneId: string) => boolean;
  footerSlot?: ReactNode;
  crosshair?: boolean;
  projectId?: string;
  designPackageName?: string;
  onSetRequiredOnSite?: (bidPackageId: string, isoDate: string | null) => void;
  /** Opens Manage Bid Packages from the empty-state heading. */
  onManageBidPackages?: () => void;
  /** Opens one bid package's details page (its card's No Plan "Create Plan"). */
  onCreatePlan?: (bidPackageId: string) => void;
  /** Read-only print: size the lane so the required-on-site column is on the page. */
  printMode?: boolean;
  /** Draw bid package dates after the design span to scale instead of in the
   *  right overflow zone. The left zone is unchanged. */
  fullTimeline?: boolean;
}) {
  const {
    geometry: layoutGeometry,
    config,
    onAddMilestone,
    onMilestoneReschedule,
    onMilestoneActualDate,
    onRenameMilestone,
    onDeleteMilestone,
    milestoneScopeItems,
    onMilestoneScopeOpen,
    bidScopeItems,
    onBidScopeOpen,
    onLinkChange,
    canLinkBidToMilestone,
    footerSlot,
    crosshair,
    projectId,
    designPackageName = "",
    onSetRequiredOnSite,
    onManageBidPackages,
    onCreatePlan,
    printMode = false,
    fullTimeline = false
  } = props;
  const portalTarget = usePlannerPortalTarget();
  const [query, setQuery] = useState("");
  const [bidSort, setBidSort] = useState<BidSort>("earliest");
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuTab, setMenuTab] = useState<MenuTab>("filter");
  const [hiddenBidIds, setHiddenBidIds] = useState<Set<string>>(
    () => new Set()
  );
  const [deletingMilestone, setDeletingMilestone] =
    useState<MilestoneNode | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [linkHover, setLinkHover] = useState<{
    type: "card" | "link";
    id: string;
  } | null>(null);
  // Bid package just re-linked by a drop: its "Drag to change the milestone"
  // hint stays hidden until the pointer comes back to its ball.
  const [relinkedId, setRelinkedId] = useState<string | null>(null);
  // Card ball under the pointer: its "Drag to change the milestone" hint
  // shows only then.
  const [hoveredBallId, setHoveredBallId] = useState<string | null>(null);
  const linkHoverTimer = useRef<number | null>(null);
  /** Milestone picked from its link count: its links and cards stay lit and
   *  everything else dims, until cleared. */
  const [activeMilestoneId, setActiveMilestoneId] = useState<string | null>(
    null
  );
  const [hoveredMilestoneId, setHoveredMilestoneId] = useState<string | null>(
    null
  );
  const [hoverCard, setHoverCard] = useState<{
    milestoneId: string;
    left: number;
    top: number;
  } | null>(null);
  const closeTimer = useRef<number | null>(null);
  const cardLockedRef = useRef(false);
  const hoverMilestoneIdRef = useRef<string | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const chartScrollRef = useRef<HTMLDivElement>(null);
  const rowsScrollRef = useRef<HTMLDivElement>(null);
  const rosListRef = useRef<HTMLDivElement>(null);
  const centeredSpanKey = useRef<string | null>(null);
  const bpListRef = useRef<HTMLDivElement>(null);
  const scrollLock = useRef(false);
  const [cursor, setCursor] = useState<{ left: string; label: string } | null>(
    null
  );
  const [rowsScrollTop, setRowsScrollTop] = useState(0);
  // Visible height of the bid-row pane. 0 until measured.
  const [rowsViewH, setRowsViewH] = useState(0);
  // Legend "Show" switches, shared by every design package until a reload.
  // Start lines are off until turned on.
  const [showStartLines, setShowStartLines] = useTimelineViewPref("startLines");
  const [showLinkLines, setShowLinkLines] = useTimelineViewPref("linkLines");
  const [showDateCursor, setShowDateCursor] = useTimelineViewPref("dateCursor");
  // Turned off here or in another lane: drop a date already shown.
  useEffect(() => {
    if (!showDateCursor) setCursor(null);
  }, [showDateCursor]);
  // The bid-row pane's vertical scrollbar makes its visible width smaller than
  // the date chart. This keeps both panes on the same horizontal range.
  const [scrollGutter, setScrollGutter] = useState(0);
  // Height of the bid-row pane's horizontal scrollbar, so the Required on site
  // list (which then drives vertical scrolling) scrolls exactly as far.
  const [rowsScrollbarH, setRowsScrollbarH] = useState(0);
  // Scrollable width of the bid-row pane. Deadline labels and float chips
  // near the end of the scale can reach past trackW, so
  // the date chart takes this width to scroll exactly as far.
  const [rowsScrollW, setRowsScrollW] = useState(0);
  const [rosCollapsed, setRosCollapsed] = useState(false);
  // Right edge of the visible timeline, in track coordinates. 0 until measured.
  const [viewRight, setViewRight] = useState(0);
  const [detailBidId, setDetailBidId] = useState<string | null>(null);
  // Selecting and showing the panel are separate: the panel's X hides it and
  // leaves the row selected with its drag handles live, and only a press
  // outside drops the selection itself.
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const selectBid = useCallback((id: string) => {
    setDetailBidId(id);
    setDetailPanelOpen(true);
  }, []);
  const clearBid = useCallback(() => {
    setDetailBidId(null);
    setDetailPanelOpen(false);
  }, []);
  // The bid row under the pointer, shaded across all three columns.
  const [hoverRowId, setHoverRowId] = useState<string | null>(null);
  const rowBg = (id: string) => {
    if (id === detailBidId) return SELECTED_ROW_BG;
    return id === hoverRowId ? HOVER_ROW_BG : "";
  };
  const laneRootRef = useRef<HTMLDivElement>(null);
  // The press that clears the selection must also swallow the click it turns
  // into, so it does not act on whatever it landed on. That outlives the
  // effect below (deselecting re-runs it before the click lands), so it is
  // held here and torn down on unmount — a stray capture listener on window
  // would otherwise eat a click anywhere in the app.
  const clickSwallow = useRef<{
    fn: (event: globalThis.MouseEvent) => void;
    timer: number | null;
  } | null>(null);
  const disarmClickSwallow = useCallback(() => {
    const armed = clickSwallow.current;
    if (!armed) return;
    window.removeEventListener("click", armed.fn, true);
    if (armed.timer != null) window.clearTimeout(armed.timer);
    clickSwallow.current = null;
  }, []);
  const armClickSwallow = useCallback(() => {
    disarmClickSwallow();
    const fn = (event: globalThis.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      clickSwallow.current = null;
    };
    window.addEventListener("click", fn, { capture: true, once: true });
    // A drag that never ends in a click must not eat the next one.
    clickSwallow.current = {
      fn,
      timer: window.setTimeout(disarmClickSwallow, 600)
    };
  }, [disarmClickSwallow]);
  useEffect(() => disarmClickSwallow, [disarmClickSwallow]);
  // A press anywhere outside the selected bid package (its card and the
  // detail panel, or a popup opened from that panel) clears the selection
  // and closes the panel. Inside the lane, that press does nothing else: the
  // other timeline interactions wait until the selection is cleared. Pressing
  // another bid card is the exception — it moves the selection straight
  // there, so switching packages never costs a throwaway click.
  useEffect(() => {
    if (!detailBidId) return undefined;
    const keep = [
      // The selected card, its row across all three columns, its drag
      // handle, hint and link edit.
      `[data-testid$="-${detailBidId}"]`,
      '[data-testid="planner-bid-detail"]',
      ".ant-modal-root",
      ".ant-select-dropdown",
      ".ant-picker-dropdown",
      ".ant-dropdown",
      ".ant-popover",
      ".ant-message",
      ".ant-notification"
    ].join(", ");
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target?.isConnected || target.closest(keep)) return;
      const otherCard = target.closest(`[data-testid^="${BID_BAR_TESTID}"]`);
      const otherId = otherCard
        ? (otherCard.getAttribute("data-testid") ?? "").slice(
            BID_BAR_TESTID.length
          ) || null
        : null;
      if (otherId) {
        selectBid(otherId);
        setHoverRowId(null);
        return;
      }
      clearBid();
      setHoverRowId(null);
      if (laneRootRef.current?.contains(target)) armClickSwallow();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [detailBidId, armClickSwallow, selectBid, clearBid]);
  // True while any pane of the lane is scrolling (and SCROLL_IDLE_MS after).
  // Content sliding under a still pointer must not open hover cards, light
  // links or shade rows.
  const scrollingRef = useRef(false);
  const scrollIdleTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (scrollIdleTimer.current != null)
        window.clearTimeout(scrollIdleTimer.current);
    },
    []
  );
  const rowHoverHandlers = (id: string) => ({
    onMouseEnter: () => {
      if (!detailBidId && !scrollingRef.current) setHoverRowId(id);
    },
    onMouseLeave: () =>
      setHoverRowId((current) => (current === id ? null : current))
  });
  const [linkDrag, setLinkDrag] = useState<{
    bpId: string;
    pointerId: number;
    startX: number;
    startY: number;
    active: boolean;
    targetId: string | null;
    allowed: boolean;
    x: number;
    y: number;
  } | null>(null);

  // Today, from the design package's todays_date; today in the project's time
  // zone when the log row has none.
  const todayIso =
    layoutGeometry.todayDate || dayjs().tz().format(DATE_FORMAT_YYYYMMDD);
  const q = query.trim().toLowerCase();
  // The scale fits the bid packages left by the search and filter.
  const scaledRows = useMemo(
    () => layoutGeometry.rows.filter((g) => bidRowShown(g, q, hiddenBidIds)),
    [layoutGeometry.rows, q, hiddenBidIds]
  );
  const timeScale = useMemo(
    () =>
      plannerTimeScaleOf(layoutGeometry, todayIso, {
        fullTimeline,
        visibleRows: scaledRows
      }),
    [layoutGeometry, todayIso, fullTimeline, scaledRows]
  );
  // The layout's milestone and link positions, moved onto timeScale.
  const geometry = useMemo((): Geometry => {
    if (!timeScale) return layoutGeometry;
    const tickX = new Map<string, number>();
    const ticks = layoutGeometry.axis.ticks.map((tick) => {
      const date = tick.milestone.effectiveDate;
      const x = date ? timeScale.x(dayOf(date)) : tick.x;
      tickX.set(tick.milestone.id, x);
      return { ...tick, x };
    });
    return {
      ...layoutGeometry,
      axis: { ...layoutGeometry.axis, ticks },
      rows: layoutGeometry.rows.map((g) =>
        g.linkBadge
          ? {
              ...g,
              linkBadge: {
                ...g.linkBadge,
                milestoneX:
                  tickX.get(g.linkBadge.milestoneId) ?? g.linkBadge.milestoneX
              }
            }
          : g
      ),
      inScope: {
        start: timeScale.x(timeScale.firstMilestoneDay),
        end: timeScale.x(timeScale.lastMilestoneDay)
      }
    };
  }, [layoutGeometry, timeScale]);

  const baseWidth = timeScale
    ? timeScale.width
    : contentWidthOf(geometry, config);
  const showRequiredOnSite = geometry.rows.length > 0;
  // Is anything drawn after the last milestone? If not, the scale ends a short
  // TIMELINE_EMPTY_TRAIL_PX past it and no end room is added.
  const drawnPastMilestones =
    timeScale != null &&
    timeScale.linearEndDay >
      timeScale.lastMilestoneDay +
        Math.ceil(TIMELINE_EMPTY_TRAIL_PX / timeScale.pxPerDay);
  // The date scale already leaves a minimum bid-card width after the last
  // milestone. Required on Site is fixed outside this track, so a wider
  // gutter would only add empty space before that column.
  const trackEndGap = printMode
    ? 16
    : showRequiredOnSite || (timeScale && !drawnPastMilestones)
      ? 12
      : TRACK_END_GAP;
  const timelineCenter =
    geometry.inScope.end > geometry.inScope.start
      ? (geometry.inScope.start + geometry.inScope.end) / 2
      : baseWidth / 2;
  const spanPins = useMemo(
    () =>
      geometry.inScope.end > geometry.inScope.start
        ? { firstX: geometry.inScope.start, lastX: geometry.inScope.end }
        : null,
    [geometry.inScope.start, geometry.inScope.end]
  );
  const ticks = geometry.axis.ticks.filter((t) => Boolean(t.milestone.name));
  const labelLayout = milestoneLabelLayout(
    ticks.map((tick) => {
      const actual = tick.milestone.actualDate;
      const shown = actual || tick.milestone.effectiveDate;
      return {
        x: tick.x,
        name: tick.milestone.name,
        sub: shown
          ? `${actual ? "Actual" : "Committed"} ${fmtCommitted(shown)}`
          : ""
      };
    })
  );
  const labelSides = labelLayout.sides;
  const offsetInPin = new Map<string, number>();
  ticks.forEach((tick, index) => {
    const next = ticks[index + 1];
    const days = tick.milestone.offset?.duration ?? null;
    if (!next || days == null || days <= 0) return;
    if (offsetFit(next.x - tick.x) === "pin") {
      offsetInPin.set(tick.milestone.id, days);
    }
  });
  const milestones = ticks.map((t) => t.milestone);
  const dated = ticks
    .map((t) => t.milestone.effectiveDate)
    .filter((d): d is string => Boolean(d))
    .sort((left, right) => left.localeCompare(right));
  const range =
    dated.length > 0
      ? `${fmtCommitted(dated[0])} → ${fmtCommitted(dated[dated.length - 1])}`
      : "";

  const issuedTo =
    [...ticks].reverse().find((t) => Boolean(t.milestone.actualDate))?.x ??
    null;
  const nextDelay = nextMilestoneDelay(milestones, geometry.currentDelay);

  const rows = useMemo(() => {
    return geometry.rows
      .filter((g) => bidRowShown(g, q, hiddenBidIds))
      .sort((a, b) => compareBidRows(a, b, bidSort));
  }, [geometry.rows, q, bidSort, hiddenBidIds]);
  // Each card is placed once per data change, not on every render (the date
  // cursor re-renders on every mouse move).
  const cardById = useMemo(() => {
    const cards = new Map<string, TimelineCard | null>();
    rows.forEach((g) => {
      cards.set(g.row.id, timelineCard(g, spanPins, timeScale));
    });
    return cards;
  }, [rows, spanPins, timeScale]);
  const placeCard = (g: BidPackageRowGeometry) =>
    cardById.get(g.row.id) ?? null;
  const cardRight = rows.reduce((max, g) => {
    const card = placeCard(g);
    return card ? Math.max(max, card.x + card.width) : max;
  }, 0);
  // The drawn scale, room after a right overflow zone (or the full timeline's
  // last date past the last milestone) for the Deadline label and float chip,
  // and every card at its full (minimum) width.
  const width = Math.max(
    baseWidth +
      (timeScale?.right || (fullTimeline && drawnPastMilestones)
        ? TIMELINE_END_PAD
        : 0),
    cardRight
  );
  const baseTrackW = width + trackEndGap;
  const trackW = Math.max(baseTrackW, cardRight + trackEndGap);
  // The selected bid package's details take Required on site's place.
  const detailOpen =
    !printMode &&
    detailPanelOpen &&
    detailBidId != null &&
    geometry.rows.some((g) => g.row.id === detailBidId);
  const rosShown = showRequiredOnSite && !detailOpen;
  const rosOpen = rosShown && (printMode || !rosCollapsed);
  const rosW = detailOpen
    ? BID_DETAIL_PANEL_W
    : rosShown
      ? rosOpen
        ? ROS_COL_W
        : ROS_COLLAPSED_W
      : 0;
  // Required on site, open or tucked into its rail, carries the vertical
  // scrollbar at the panel's right edge and the bid-row pane follows it.
  const rosScrolls = rosShown && !printMode;
  const framedW = trackW + rosW;
  const bidListFiltered = Boolean(q) || hiddenBidIds.size > 0;
  const visibleBidCount = rows.length;
  const totalBidCount = geometry.rows.length;
  // Milestones linked to a bid package still in the list. While the list is
  // filtered, the other milestones' link-count chips fade.
  const visibleLinkedMilestones = useMemo(
    () => new Set(rows.map((g) => g.linkBadge?.milestoneId).filter(Boolean)),
    [rows]
  );
  // A filter that hides every bid package of the active milestone clears it.
  useEffect(() => {
    if (activeMilestoneId && !visibleLinkedMilestones.has(activeMilestoneId)) {
      setActiveMilestoneId(null);
    }
  }, [activeMilestoneId, visibleLinkedMilestones]);
  // A click anywhere but a link count, or Escape, clears the active milestone.
  useEffect(() => {
    if (!activeMilestoneId) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (target?.closest?.("[data-ms-link-count]")) return;
      setActiveMilestoneId(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActiveMilestoneId(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [activeMilestoneId]);

  const overflowZones = [timeScale?.left, timeScale?.right].filter(
    (zone): zone is OverflowZone => Boolean(zone)
  );
  // Today is always on the scale: a Today outside the margins opens a zone.
  const todayX = timeScale ? timeScale.x(dayOf(todayIso)) : null;
  const showToday = todayX != null && todayX >= 0 && todayX <= width;
  const marks = useMemo(
    () => axisMarks(timeScale, fullTimeline, width, showToday ? todayX : null),
    [timeScale, fullTimeline, width, showToday, todayX]
  );
  const linkCounts = useMemo(() => {
    const counts = new Map<string, number>();
    geometry.rows.forEach((g) => {
      const id = g.linkBadge?.milestoneId;
      if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
    });
    return counts;
  }, [geometry.rows]);
  const linkCount = (milestoneId: string) => linkCounts.get(milestoneId) ?? 0;

  const linkLayout = rows.flatMap((g, index) => {
    const milestoneX = g.linkBadge?.milestoneX ?? null;
    const card = placeCard(g);
    if (!card || milestoneX == null || !g.linkBadge) {
      return [];
    }
    const cy = CHART_H + index * BID_ROW_H + CARD_DY + CARD_H / 2;
    const bar: BarBox = {
      left: card.x,
      right: card.x + card.width,
      top: cy - CARD_H / 2,
      cy
    };
    return [
      {
        g,
        milestoneId: g.linkBadge.milestoneId,
        mx: milestoneX,
        bar
      }
    ];
  });
  // A dashed line from each card's start up to the date axis. Unplanned cards
  // are parked beside the design span, so their x is not a start date.
  const startLines = showStartLines
    ? rows.flatMap((g, index) => {
        const card = placeCard(g);
        if (!card || card.unplanned) return [];
        return [
          {
            id: g.row.id,
            x: card.x,
            cardTop: CHART_H + index * BID_ROW_H + CARD_DY,
            color: START_LINE_COLOR
          }
        ];
      })
    : [];

  // Bid packages whose card is scrolled fully out of the rows pane.
  let hiddenAbove = 0;
  let lastAboveIndex = -1;
  let hiddenBelow = 0;
  let firstBelowIndex = -1;
  if (!printMode && rowsViewH > 0) {
    rows.forEach((_, index) => {
      const top = index * BID_ROW_H + CARD_DY;
      if (top + CARD_H <= rowsScrollTop) {
        hiddenAbove += 1;
        lastAboveIndex = index;
      } else if (top >= rowsScrollTop + rowsViewH) {
        hiddenBelow += 1;
        if (firstBelowIndex < 0) firstBelowIndex = index;
      }
    });
  }
  const scrollRowIntoView = (index: number, edge: "top" | "bottom") => {
    const rowScroller = rowsScrollRef.current;
    if (!rowScroller) return;
    const top =
      edge === "top"
        ? index * BID_ROW_H
        : (index + 1) * BID_ROW_H - rowScroller.clientHeight;
    rowScroller.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  };

  const dragActive = Boolean(linkDrag?.active);
  // While a milestone is active, everything not linked to it dims.
  const highlightOn = activeMilestoneId != null && !dragActive;
  // The link in its re-link state: the selected bid package's, otherwise the
  // hovered one's.
  const relinkId = detailBidId ?? linkHover?.id ?? null;
  const litIds = highlightedBidIds({
    dragActive,
    activeMilestoneId,
    linkHoverId: relinkId,
    hoveredMilestoneId,
    linkLayout
  });
  const litMilestones = new Set(
    linkLayout.filter((l) => litIds.has(l.g.row.id)).map((l) => l.milestoneId)
  );
  /** Opacity of a bid package's link: an active milestone dims every other
   *  link; otherwise a lit link fades the unlit links sharing its stem. */
  const linkOpacity = (id: string, milestoneId: string) => {
    const lit = litIds.has(id);
    if (highlightOn) return lit ? 1 : HIGHLIGHT_DIM;
    const faded = !lit && (litMilestones.has(milestoneId) || dragActive);
    return faded ? 0.3 : 1;
  };
  // Links sharing a milestone overlap on its stem, each running down to its
  // own card. Unlit links are drawn bottom row first, so each row's stroke
  // covers the stem only down to its card and the next row's colour shows
  // below it. The lit ones go last so that, together with their higher
  // z-index, their whole stem and elbow stay on top.
  const drawnLinks = showLinkLines
    ? [
        ...linkLayout.filter((l) => !litIds.has(l.g.row.id)).reverse(),
        ...linkLayout.filter((l) => litIds.has(l.g.row.id))
      ]
    : [];
  const dragLink = dragActive
    ? linkLayout.find((l) => l.g.row.id === linkDrag?.bpId)
    : undefined;
  const dragTick = dragActive
    ? ticks.find((t) => t.milestone.id === linkDrag?.targetId)
    : undefined;
  // Hovering a link does not light it: only its card (hover or selection)
  // does. A lit link stays lit while the pointer runs along it to its handle.
  const linkHoverHandlers = (id: string) => ({
    onMouseEnter: () => {
      if (!linkDrag && relinkId === id) hoverLink({ type: "link", id });
    },
    onMouseLeave: () => {
      if (relinkId === id) hoverLink(null);
    }
  });

  const segments = (
    id: string,
    pts: Pt[],
    look: {
      color: string;
      lineW: number;
      dash: [number, number] | null;
      opacity: number;
      z: number;
    }
  ) =>
    pts.slice(1).map((p, i) => {
      const [x1, y1] = pts[i];
      const [x2, y2] = p;
      const vertical = x1 === x2;
      const len = vertical ? Math.abs(y2 - y1) : Math.abs(x2 - x1);
      if (len <= 0) return null;
      const background = look.dash
        ? dashes(look.color, vertical ? "bottom" : "right", ...look.dash)
        : look.color;
      return (
        <div
          key={`seg-${i}`}
          data-testid={
            i === 0
              ? `planner-bp-link-line-${id}`
              : i === 1
                ? `planner-bp-link-elbow-${id}`
                : `planner-bp-link-elbow-${id}-${i}`
          }
          data-stroke={look.color}
          data-dashed={look.dash ? true : undefined}
          className={`absolute transition-opacity duration-150 ${vertical ? "-translate-x-1/2" : "-translate-y-1/2"}`}
          style={
            vertical
              ? {
                  left: x1,
                  top: Math.min(y1, y2),
                  width: look.lineW,
                  height: len,
                  background,
                  opacity: look.opacity,
                  zIndex: look.z
                }
              : {
                  left: Math.min(x1, x2),
                  top: y1,
                  width: len,
                  height: look.lineW,
                  background,
                  opacity: look.opacity,
                  zIndex: look.z
                }
          }
          {...linkHoverHandlers(id)}
        />
      );
    });

  const dragMark = () => {
    if (!linkDrag || !dragLink) return null;
    const { bar, mx } = dragLink;
    const rest = linkPoints(mx, bar, REST_TOP);
    const [ax, ay] = rest[rest.length - 1];
    // Pin stays in the chart while the rows scroll, so its track y includes scrollTop.
    const [ex, ey]: Pt = dragTick
      ? [dragTick.x, PIN_Y + rowsScrollTop]
      : [linkDrag.x, linkDrag.y];
    // The link always leaves the card's start, so the curve heads left.
    const c1: Pt = [ax - 120, ay];
    return { ax, ay, ex, ey, c1 };
  };

  const dragOverlay = () => {
    const mark = dragMark();
    if (!linkDrag || !dragLink || !mark) return null;
    const { g } = dragLink;
    const { ax, ay, ex, ey, c1 } = mark;
    const label = g.row.number || g.row.name;
    const milestone = dragTick?.milestone;
    let tip: ReactNode;
    if (!milestone) {
      tip = (
        <LinkChip
          testId="planner-bp-link-tip"
          kind="dark"
          x={linkDrag.x + 16}
          y={linkDrag.y + 16}
          title="Drop on a milestone"
          detail={`to link ${g.row.name || g.row.number}`}
        />
      );
    } else if (!linkDrag.allowed) {
      tip = (
        <LinkChip
          testId="planner-bp-link-tip"
          kind="warn"
          x={ex + 20}
          y={ey + 26}
          title="Scope not in this milestone"
          detail={`${label} can't link to ${milestone.name}`}
        />
      );
    } else if (dragCaution) {
      tip = (
        <LinkChip
          testId="planner-bp-link-tip"
          kind="warn"
          x={ex + 20}
          y={ey + 26}
          title={`Link ${label} to ${milestone.name}`}
          detail={dragCaution}
        />
      );
    } else {
      const date = milestone.actualDate || milestone.effectiveDate;
      tip = (
        <LinkChip
          testId="planner-bp-link-tip"
          kind="light"
          x={ex + 20}
          y={ey + 26}
          title={`Link to ${milestone.name}`}
          detail={
            date
              ? `${milestone.actualDate ? "Actual" : "Committed"} ${fmtCommitted(date)}`
              : undefined
          }
        />
      );
    }
    return (
      <>
        {dragCaution ? (
          <div
            data-testid="planner-bp-link-warn-column"
            className="pointer-events-none absolute bottom-0 z-[5] w-0.5 -translate-x-1/2"
            style={{
              left: ex,
              top: CHART_H,
              background: dashes("var(--ciq-red-400)", "bottom", 5, 4)
            }}
          />
        ) : null}
        {createPortal(
          <DragCurve
            ax={ax + (trackBox?.left ?? 0)}
            ay={ay + (trackBox?.top ?? 0)}
            c1x={c1[0] + (trackBox?.left ?? 0)}
            c1y={c1[1] + (trackBox?.top ?? 0)}
            ex={ex + (trackBox?.left ?? 0)}
            ey={ey + (trackBox?.top ?? 0)}
            snapped={Boolean(milestone)}
          />,
          document.body
        )}
        {trackBox
          ? createPortal(
              <div
                className="pointer-events-none fixed z-[2100]"
                style={{ left: trackBox.left, top: trackBox.top }}
              >
                {tip}
              </div>,
              document.body
            )
          : tip}
        {createPortal(
          <div className="fixed inset-0 z-[2000] cursor-grabbing" />,
          document.body
        )}
      </>
    );
  };

  const addMilestone = () => {
    if (!onAddMilestone) return;
    const last = dated[dated.length - 1];
    const click = last ? isoOfDay(dayOf(last) + 14) : undefined;
    if (!click) return;
    const placement = placementForDate(milestones, `${click}T00:00:00Z`);
    if (placement) onAddMilestone(placement);
  };

  const rememberTimelineEdge = (rowScroller: HTMLElement) => {
    const next = rowScroller.scrollLeft + rowScroller.clientWidth;
    setViewRight((current) => (current === next ? current : next));
    const scrollW = rowScroller.scrollWidth;
    setRowsScrollW((current) => (current === scrollW ? current : scrollW));
    const viewH = rowScroller.clientHeight;
    setRowsViewH((current) => (current === viewH ? current : viewH));
  };

  const syncBidScroll = (source: "list" | "rows" | "ros" | "chartX") => {
    if (scrollLock.current) return;
    markScrolling();
    const chart = chartScrollRef.current;
    const rowScroller = rowsScrollRef.current;
    const list = bpListRef.current;
    const rosList = rosListRef.current;
    scrollLock.current = true;
    if (source === "list" && list && rowScroller) {
      rowScroller.scrollTop = list.scrollTop;
      if (rosList) rosList.scrollTop = list.scrollTop;
    }
    if (source === "ros" && rosList && rowScroller) {
      rowScroller.scrollTop = rosList.scrollTop;
      if (list) list.scrollTop = rosList.scrollTop;
    }
    if (source === "rows" && rowScroller) {
      if (list) list.scrollTop = rowScroller.scrollTop;
      if (rosList) rosList.scrollTop = rowScroller.scrollTop;
      if (chart) chart.scrollLeft = rowScroller.scrollLeft;
      const top = rowScroller.scrollTop;
      const gutter = Math.max(
        0,
        rowScroller.offsetWidth - rowScroller.clientWidth
      );
      setRowsScrollTop((current) => (current === top ? current : top));
      setScrollGutter((current) => (current === gutter ? current : gutter));
      rememberTimelineEdge(rowScroller);
    }
    if (source === "chartX" && chart && rowScroller) {
      rowScroller.scrollLeft = chart.scrollLeft;
      rememberTimelineEdge(rowScroller);
    }
    scrollLock.current = false;
  };

  const onTimelineMove = (event: MouseEvent<HTMLDivElement>) => {
    if (!crosshair || !showDateCursor || scrollingRef.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left + event.currentTarget.scrollLeft;
    if (!timeScale || x < 0 || x > trackW) {
      setCursor(null);
      return;
    }
    const point = timeScale.at(x);
    setCursor({
      left: `${x}px`,
      label:
        point.kind === "before"
          ? `Before ${fmtZoneEdge(point.day)}`
          : point.kind === "after"
            ? `After ${fmtZoneEdge(point.day)}`
            : fmtAxis(isoOfDay(point.day))
    });
  };

  const hoverLink = (next: { type: "card" | "link"; id: string } | null) => {
    if (linkHoverTimer.current != null) {
      window.clearTimeout(linkHoverTimer.current);
      linkHoverTimer.current = null;
    }
    if (next) {
      if (scrollingRef.current) return;
      if (next.type === "card") setRelinkedId(null);
      // A selected bid package holds the others still until it is cleared;
      // its own link and drag handle keep working.
      if (!detailBidId || next.id === detailBidId) setLinkHover(next);
      return;
    }
    linkHoverTimer.current = window.setTimeout(() => {
      setLinkHover(null);
    }, 80);
  };

  useEffect(
    () => () => {
      if (linkHoverTimer.current != null)
        window.clearTimeout(linkHoverTimer.current);
    },
    []
  );

  const toTrack = (clientX: number, clientY: number): Pt | null => {
    const track = trackRef.current;
    if (!track) return null;
    const rect = track.getBoundingClientRect();
    const x = clientX - rect.left;
    const scrollTop = rowsScrollRef.current?.scrollTop ?? 0;
    const chartBox = chartScrollRef.current?.getBoundingClientRect();
    const rowsBox = rowsScrollRef.current?.getBoundingClientRect();
    if (
      chartBox &&
      chartBox.height > 0 &&
      clientY >= chartBox.top &&
      clientY <= chartBox.bottom
    ) {
      return [x, clientY - chartBox.top + scrollTop];
    }
    if (rowsBox && rowsBox.height > 0) {
      return [x, CHART_H + (clientY - rowsBox.top) + scrollTop];
    }
    return [x, clientY - rect.top];
  };

  const snapMilestone = (x: number, y: number) => {
    const scrollTop = rowsScrollRef.current?.scrollTop ?? 0;
    const pinY = PIN_Y + scrollTop;
    let nearest: (typeof ticks)[number] | null = null;
    let nearestDist = SNAP_R;
    for (const tick of ticks) {
      const dist = Math.hypot(tick.x - x, pinY - y);
      if (dist <= nearestDist) {
        nearestDist = dist;
        nearest = tick;
      }
    }
    if (nearest) return nearest;
    if (y > scrollTop && y < pinY + 12) {
      return (
        ticks.find((tick) => x >= tick.x - 120 && x <= tick.x + 16) ?? null
      );
    }
    return null;
  };

  const bidStart = (bpId: string) =>
    geometry.rows
      .find((g) => g.row.id === bpId)
      ?.row.milestones.map((m) => m.effectiveDate)
      .filter((d): d is string => Boolean(d))
      .sort((left, right) => left.localeCompare(right))[0] ?? null;

  const cautionFor = (bpId: string, milestoneId: string | null) => {
    if (!milestoneId) return null;
    const target = ticks.find((t) => t.milestone.id === milestoneId)?.milestone
      .effectiveDate;
    const start = bidStart(bpId);
    if (!target || !start) return null;
    const days = daysBefore(start, target);
    if (days <= 0) return null;
    return `Start date is ${days} day${days === 1 ? "" : "s"} before Milestone`;
  };

  const onLinkHandleDown = (
    bpId: string,
    event: ReactPointerEvent<HTMLButtonElement>
  ) => {
    if (event.button !== 0) return;
    // Only the selected bid package, if any, can be dragged to a milestone.
    if (detailBidId && detailBidId !== bpId) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setLinkDrag({
      bpId,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      active: false,
      targetId: null,
      allowed: true,
      x: 0,
      y: 0
    });
  };

  const linkDragRef = useRef(linkDrag);
  linkDragRef.current = linkDrag;

  // While a pin is held against either edge of the visible timeline, keep
  // scrolling so milestones that were off-screen can be reached.
  const nudgeLinkScroll = (clientX: number) => {
    const rowScroller = rowsScrollRef.current;
    const chart = chartScrollRef.current;
    const rowBox = rowScroller?.getBoundingClientRect();
    const box =
      rowBox && rowBox.width > 0 ? rowBox : chart?.getBoundingClientRect();
    if (!rowScroller || !box || box.width <= 0) return false;
    const fromLeft = clientX - box.left;
    const fromRight = box.right - clientX;
    let direction = 0;
    let depth = 0;
    if (
      fromLeft <= LINK_SCROLL_EDGE &&
      (fromRight > LINK_SCROLL_EDGE || fromLeft <= fromRight)
    ) {
      direction = -1;
      depth = Math.min(1, (LINK_SCROLL_EDGE - fromLeft) / LINK_SCROLL_EDGE);
    } else if (fromRight <= LINK_SCROLL_EDGE) {
      direction = 1;
      depth = Math.min(1, (LINK_SCROLL_EDGE - fromRight) / LINK_SCROLL_EDGE);
    }
    if (direction === 0) return false;
    const step = direction * Math.max(6, Math.round(6 + depth * 12));
    const max = Math.max(0, rowScroller.scrollWidth - rowScroller.clientWidth);
    const next = Math.min(max, Math.max(0, rowScroller.scrollLeft + step));
    if (next === rowScroller.scrollLeft) return false;
    rowScroller.scrollLeft = next;
    if (chart) chart.scrollLeft = next;
    return true;
  };

  useEffect(() => {
    if (!linkDrag) return undefined;
    const pointer = { x: 0, y: 0, seen: false };
    const placeDrag = (clientX: number, clientY: number) => {
      const drag = linkDragRef.current;
      if (!drag) return;
      const point = toTrack(clientX, clientY);
      if (!point) return;
      const nearest = snapMilestone(point[0], point[1]);
      const allowed = nearest
        ? (canLinkBidToMilestone?.(drag.bpId, nearest.milestone.id) ?? true)
        : true;
      if (!drag.active) setLinkHover(null);
      setLinkDrag((current) =>
        current && current.pointerId === drag.pointerId
          ? {
              ...current,
              active: true,
              targetId: nearest?.milestone.id ?? null,
              allowed,
              x: point[0],
              y: point[1]
            }
          : current
      );
    };
    const onMove = (event: PointerEvent) => {
      const drag = linkDragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      pointer.seen = true;
      if (
        !drag.active &&
        Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) <
          DRAG_START
      ) {
        return;
      }
      nudgeLinkScroll(event.clientX);
      placeDrag(event.clientX, event.clientY);
    };
    let frame = 0;
    const pump = () => {
      frame = window.requestAnimationFrame(pump);
      const drag = linkDragRef.current;
      if (!drag?.active || !pointer.seen) return;
      if (!nudgeLinkScroll(pointer.x)) return;
      placeDrag(pointer.x, pointer.y);
    };
    frame = window.requestAnimationFrame(pump);
    const onUp = (event: PointerEvent) => {
      const drag = linkDragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      setLinkDrag(null);
      if (!drag.active) return;
      const currentId = geometry.rows.find((row) => row.row.id === drag.bpId)
        ?.linkBadge?.milestoneId;
      if (drag.targetId && drag.allowed && drag.targetId !== currentId) {
        const caution = cautionFor(drag.bpId, drag.targetId);
        if (caution) onLinkChange?.(drag.bpId, drag.targetId, { caution });
        else onLinkChange?.(drag.bpId, drag.targetId);
        // The link is done: drop the hover and its drag hint.
        setLinkHover(null);
        setRelinkedId(drag.bpId);
      }
    };
    const onCancel = (event: PointerEvent) => {
      if (event.pointerId === linkDragRef.current?.pointerId) setLinkDrag(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setLinkDrag(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("keydown", onKey);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("keydown", onKey);
    };
    // Listeners stay for the life of one drag. The latest target lives on the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkDrag?.bpId, linkDrag?.pointerId]);

  useEffect(() => {
    const rowScroller = rowsScrollRef.current;
    if (!rowScroller) return undefined;
    const measure = () => {
      const next = Math.max(
        0,
        rowScroller.offsetWidth - rowScroller.clientWidth
      );
      setScrollGutter((current) => (current === next ? current : next));
      const barH = Math.max(
        0,
        rowScroller.offsetHeight - rowScroller.clientHeight
      );
      setRowsScrollbarH((current) => (current === barH ? current : barH));
      rememberTimelineEdge(rowScroller);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(rowScroller);
    return () => observer.disconnect();
  }, [rows.length, trackW, rosScrolls]);

  // Re-opened Required on site starts at the top; bring it level with the rows.
  useLayoutEffect(() => {
    const rosList = rosListRef.current;
    const rowScroller = rowsScrollRef.current;
    if (rosScrolls && rosList && rowScroller) {
      rosList.scrollTop = rowScroller.scrollTop;
    }
  }, [rosScrolls, rosOpen]);

  useEffect(() => {
    const chart = chartScrollRef.current;
    const rowScroller = rowsScrollRef.current;
    if (!chart || !rowScroller || chart.scrollLeft === rowScroller.scrollLeft) {
      return;
    }
    chart.scrollLeft = rowScroller.scrollLeft;
  }, [scrollGutter, rowsScrollW]);

  // Switching full timeline, or a new range (a filter or search changes the
  // zones and margins too), keeps the date at the centre of the view where it
  // was (a date in the right zone moves to its real x).
  const shownScaleRef = useRef<{
    scale: PlannerTimeScale | null;
    fullTimeline: boolean;
  } | null>(null);
  useLayoutEffect(() => {
    const shown = shownScaleRef.current;
    shownScaleRef.current = { scale: timeScale, fullTimeline };
    if (printMode || !shown?.scale || !timeScale) return;
    if (shown.scale === timeScale) return;
    // Not opened on the span yet: leave that to the centring below.
    if (centeredSpanKey.current == null) return;
    const rowScroller = rowsScrollRef.current;
    const view = rowScroller?.clientWidth ?? 0;
    if (!rowScroller || view <= 0) return;
    const day = shown.scale.dayAt(rowScroller.scrollLeft + view / 2);
    const next = Math.max(0, timeScale.x(day) - view / 2);
    rowScroller.scrollLeft = next;
    if (chartScrollRef.current) chartScrollRef.current.scrollLeft = next;
    rememberTimelineEdge(rowScroller);
  });

  // Open on the design-package span, not the leading blank margin. Resize
  // can report 0 width on the first pass, so keep watching until it lands.
  useLayoutEffect(() => {
    if (printMode) return undefined;
    const rowScroller = rowsScrollRef.current;
    if (!rowScroller) return undefined;
    const key = `${geometry.inScope.start}:${geometry.inScope.end}`;
    const center = () => {
      if (centeredSpanKey.current === key) return;
      const view = rowScroller.clientWidth;
      if (view <= 0) return;
      const next = Math.max(0, timelineCenter - view / 2);
      rowScroller.scrollLeft = next;
      if (chartScrollRef.current) chartScrollRef.current.scrollLeft = next;
      rememberTimelineEdge(rowScroller);
      centeredSpanKey.current = key;
    };
    center();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(center);
    observer.observe(rowScroller);
    return () => observer.disconnect();
  }, [printMode, geometry.inScope.start, geometry.inScope.end, timelineCenter]);

  // A sideways trackpad swipe over a pane that does not scroll horizontally, or
  // past either end of the timeline, is otherwise a browser Back/Forward.
  useEffect(() => {
    if (printMode) return undefined;
    const rowScroller = rowsScrollRef.current;
    const nodes = [
      rowScroller,
      chartScrollRef.current,
      bpListRef.current,
      rosListRef.current
    ].filter((node): node is HTMLDivElement => node != null);
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      const scroller = rowsScrollRef.current;
      if (event.currentTarget !== scroller) {
        event.preventDefault();
        return;
      }
      if (!scroller) return;
      const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const atStart = scroller.scrollLeft <= 0 && event.deltaX < 0;
      const atEnd = scroller.scrollLeft >= max - 1 && event.deltaX > 0;
      if (atStart || atEnd) event.preventDefault();
    };
    nodes.forEach((node) =>
      node.addEventListener("wheel", onWheel, { passive: false })
    );
    return () => {
      nodes.forEach((node) => node.removeEventListener("wheel", onWheel));
    };
  }, [printMode, showRequiredOnSite, rosOpen]);

  const clearCardClose = () => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  useEffect(
    () => () => {
      clearCardClose();
    },
    []
  );

  const openMilestoneCard = (milestoneId: string, el: HTMLElement) => {
    // No hover card behind an open delete dialog.
    if (detailBidId || scrollingRef.current || deletingMilestone) return;
    clearCardClose();
    // Name and date edits lock the card so it stays open. Moving to a
    // different milestone cancels that edit and shows the new name.
    if (
      hoverMilestoneIdRef.current &&
      hoverMilestoneIdRef.current !== milestoneId
    ) {
      cardLockedRef.current = false;
    }
    hoverMilestoneIdRef.current = milestoneId;
    const rect = el.getBoundingClientRect();
    setHoveredMilestoneId(milestoneId);
    setHoverCard({
      milestoneId,
      left: rect.left + rect.width / 2,
      top: rect.bottom + 10
    });
  };

  const scheduleMilestoneCardClose = () => {
    if (cardLockedRef.current) return;
    clearCardClose();
    closeTimer.current = window.setTimeout(() => {
      hoverMilestoneIdRef.current = null;
      cardLockedRef.current = false;
      setHoverCard(null);
      setHoveredMilestoneId(null);
    }, 180);
  };

  // A scroll drops every hover (the milestone card and link chips are fixed to
  // the viewport and would be left behind) and holds new ones off until it
  // settles.
  const markScrolling = () => {
    if (printMode) return;
    if (scrollIdleTimer.current != null) {
      window.clearTimeout(scrollIdleTimer.current);
    }
    scrollIdleTimer.current = window.setTimeout(() => {
      scrollingRef.current = false;
      scrollIdleTimer.current = null;
    }, SCROLL_IDLE_MS);
    if (scrollingRef.current) return;
    scrollingRef.current = true;
    if (linkHoverTimer.current != null) {
      window.clearTimeout(linkHoverTimer.current);
      linkHoverTimer.current = null;
    }
    setLinkHover(null);
    setHoverRowId(null);
    setCursor(null);
    if (!cardLockedRef.current) {
      clearCardClose();
      hoverMilestoneIdRef.current = null;
      setHoverCard(null);
      setHoveredMilestoneId(null);
    }
  };

  // Selecting a bid package drops whatever hover the others were showing.
  useEffect(() => {
    if (!detailBidId) return;
    setLinkHover((current) => (current?.id === detailBidId ? current : null));
    setHoverRowId(null);
    if (!cardLockedRef.current) {
      hoverMilestoneIdRef.current = null;
      setHoverCard(null);
      setHoveredMilestoneId(null);
    }
  }, [detailBidId]);

  const dragCaution =
    dragTick && linkDrag?.allowed && dragLink
      ? cautionFor(dragLink.g.row.id, dragTick.milestone.id)
      : null;
  const trackBox = trackRef.current?.getBoundingClientRect();

  function plannerLaneBody() {
    const hoveredMilestone = hoverCard
      ? milestones.find((m) => m.id === hoverCard.milestoneId)
      : undefined;
    const activeDrag = dragMark();
    // The link in its re-link state, with a draggable ball at every end.
    const relink =
      onLinkChange && relinkId && !dragActive
        ? linkLayout.find((item) => item.g.row.id === relinkId)
        : undefined;
    const cardLink = relink;
    const detailRow = detailBidId
      ? geometry.rows.find((item) => item.row.id === detailBidId)
      : undefined;
    return (
      <div
        ref={laneRootRef}
        data-testid="planner-lane-body"
        className={`flex h-full min-h-0 flex-1 flex-col bg-white ${
          printMode ? "w-max" : ""
        }`}
        style={printMode ? { minWidth: LABEL_W + framedW } : undefined}
      >
        <div className="flex min-h-0 flex-1">
          <div
            className="relative flex h-full min-h-0 shrink-0 flex-col border-0 border-r border-solid border-ciq-gray-100 bg-white"
            style={{ width: LABEL_W }}
          >
            <div
              className="flex flex-col px-4 pt-[13px]"
              style={{ height: MS_HEAD_H }}
            >
              <span className="text-[13px] font-semibold leading-4 text-ciq-planner-ink">
                {milestones.length} Milestone
                {milestones.length === 1 ? "" : "s"}
              </span>
              {range ? (
                <span className="mt-[5px] truncate text-xs leading-[15px] text-ciq-planner-ink3">
                  {range}
                </span>
              ) : null}
              {onAddMilestone ? (
                <button
                  type="button"
                  className="mt-3 w-fit cursor-pointer border-none bg-transparent p-0 text-[13px] font-medium leading-4 text-ciq-planner-accent"
                  data-testid="planner-add-milestone"
                  onClick={addMilestone}
                >
                  + Add Milestone
                </button>
              ) : null}
            </div>
            <div
              className="flex flex-col border-0 border-y border-solid border-ciq-border-color-1 px-4 pt-[11px]"
              style={{ height: CHART_H - MS_HEAD_H }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center">
                  <span className="truncate text-[13px] font-semibold leading-4 text-ciq-planner-ink">
                    Bid Packages
                  </span>
                  <span
                    className="ml-2 inline-flex h-5 min-w-6 shrink-0 items-center justify-center rounded-full border border-solid border-ciq-gray-300 bg-white px-1.5 text-xs text-ciq-black-a45"
                    data-testid="planner-bid-count"
                  >
                    {bidListFiltered
                      ? `${visibleBidCount} of ${totalBidCount}`
                      : `${totalBidCount}`}
                  </span>
                </span>
                <Dropdown
                  open={menuOpen}
                  onOpenChange={(open) => {
                    setMenuOpen(open);
                    if (open) setMenuTab("filter");
                  }}
                  trigger={["click"]}
                  placement="bottomRight"
                  align={{ offset: [12, 0] }}
                  popupRender={() => (
                    <div className="pl-3">
                      <BidPackageMenu
                        tab={menuTab}
                        onTab={setMenuTab}
                        sort={bidSort}
                        onSort={(next) => {
                          setBidSort(next);
                          setMenuOpen(false);
                        }}
                        packages={[...geometry.rows]
                          .sort((a, b) => compareBidRows(a, b, bidSort))
                          .map((g) => ({
                            id: g.row.id,
                            label: bidPackageLabel(g.row.number, g.row.name)
                          }))}
                        hiddenIds={hiddenBidIds}
                        onTogglePackage={(id) =>
                          setHiddenBidIds((current) => {
                            const next = new Set(current);
                            if (next.has(id)) next.delete(id);
                            else next.add(id);
                            return next;
                          })
                        }
                        onToggleAll={(ids) =>
                          setHiddenBidIds((current) =>
                            hiddenIdsAfterToggleAll(current, ids)
                          )
                        }
                      />
                    </div>
                  )}
                >
                  <button
                    type="button"
                    data-testid="planner-bp-sort"
                    aria-label={
                      hiddenBidIds.size
                        ? "Filter applied. Filter and sort Bid Packages"
                        : "Filter and sort Bid Packages"
                    }
                    className="inline-flex h-6 shrink-0 cursor-pointer items-center justify-center gap-1.5 border-none bg-transparent p-0 text-[13px] text-ciq-text-900"
                  >
                    {hiddenBidIds.size > 0 ? (
                      <FilterOutlined
                        aria-hidden
                        className="text-xs text-ciq-text-900 hover:text-ciq-text-900"
                      />
                    ) : null}
                    <MenuOutlined
                      aria-hidden
                      className="text-xs text-ciq-text-900 hover:text-ciq-text-900"
                    />
                  </button>
                </Dropdown>
              </div>
              <label className="mt-1 flex h-7 items-center gap-[7px] rounded-md border border-solid border-ciq-planner-search bg-white px-[9px]">
                <SearchOutlined className="text-[13px] text-ciq-gray-400" />
                <input
                  data-testid="planner-bp-search"
                  className="w-full min-w-0 border-none bg-transparent p-0 text-[12.5px] text-ciq-planner-ink outline-none placeholder:text-ciq-gray-400"
                  placeholder="Search Bid Packages"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>
            </div>
            <div
              ref={bpListRef}
              data-testid="planner-bp-list-scroll"
              className="min-h-0 flex-1 overflow-y-hidden overscroll-x-none"
              onScroll={() => syncBidScroll("list")}
              onWheel={(event) => {
                const rowScroller = rowsScrollRef.current;
                if (!rowScroller) return;
                rowScroller.scrollTop += event.deltaY;
                rowScroller.scrollLeft += event.deltaX;
              }}
            >
              {rows.length === 0 ? (
                <p className="px-4 py-3 text-xs text-ciq-gray-400">
                  {geometry.rows.length === 0
                    ? "Bid Packages you add will appear here."
                    : "No Bid Packages match."}
                </p>
              ) : (
                rows.map((g) => (
                  <div
                    key={g.row.id}
                    data-testid={`planner-bp-row-${g.row.id}`}
                    className={`flex flex-col justify-center border-0 border-b border-solid border-ciq-planner-row-line px-4 ${rowBg(
                      g.row.id
                    )}`}
                    style={{ height: BID_ROW_H }}
                    {...rowHoverHandlers(g.row.id)}
                  >
                    {projectId ? (
                      <Link
                        to={bidPackageDetailsPath(projectId, g.row.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`planner-bp-name-${g.row.id}`}
                        title={bidPackageLabel(g.row.number, g.row.name)}
                        className="block truncate text-sm leading-4 text-ciq-black-a88 hover:underline font-medium"
                      >
                        {bidPackageLabel(g.row.number, g.row.name)}
                      </Link>
                    ) : (
                      <span
                        className="truncate text-sm leading-4 text-ciq-planner-ink"
                        title={bidPackageLabel(g.row.number, g.row.name)}
                      >
                        {bidPackageLabel(g.row.number, g.row.name)}
                      </span>
                    )}
                    <span className="mt-1.5 text-[11px] leading-[13px] text-ciq-gray-900">
                      Linked to
                    </span>
                    <span className="relative mt-0.5 inline-flex w-full self-start">
                      <Select
                        aria-label={`Linked to for ${g.row.name || g.row.number}`}
                        data-testid={`planner-bp-link-${g.row.id}`}
                        size="small"
                        variant="borderless"
                        popupMatchSelectWidth={false}
                        getPopupContainer={() => document.body}
                        suffixIcon={
                          <DownOutlined className="text-[10px] font-semibold text-ciq-planner-accent" />
                        }
                        className="w-full -ml-2 [&_.ant-select-content]:!text-[13px] [&_.ant-select-content]:!font-semibold [&_.ant-select-content]:!leading-5 [&_.ant-select-content]:!text-ciq-planner-accent [&_.ant-select-placeholder]:text-xs [&_.ant-select-selector]:px-0"
                        placeholder="Select milestone"
                        value={g.linkBadge?.milestoneId || undefined}
                        disabled={!onLinkChange}
                        onChange={(milestoneId: string) =>
                          onLinkChange?.(g.row.id, milestoneId)
                        }
                        options={milestones.map((m: MilestoneNode) => ({
                          value: m.id,
                          label: m.name
                        }))}
                      />
                    </span>
                  </div>
                ))
              )}
              {rows.length > 0 && rowsScrollbarH > 0 ? (
                // The bid-row pane's horizontal scrollbar shortens its view, so
                // both lists reach the same last scrollTop.
                <div aria-hidden style={{ height: rowsScrollbarH }} />
              ) : null}
            </div>
          </div>
          <div
            className={`relative flex min-h-0 flex-1 flex-col ${printMode ? "" : "min-w-0"}`}
            style={printMode ? { width: framedW, flex: "none" } : undefined}
          >
            <div
              ref={chartScrollRef}
              data-testid="planner-milestone-chart-scroll"
              className={`shrink-0 ${printMode ? "overflow-visible" : "overflow-hidden overscroll-x-none"}`}
              style={{ height: CHART_H, marginRight: rosW }}
              onScroll={() => syncBidScroll("chartX")}
              onWheel={(event) => {
                const rowScroller = rowsScrollRef.current;
                if (!rowScroller) return;
                const delta = event.shiftKey ? event.deltaY : event.deltaX;
                if (delta) rowScroller.scrollLeft += delta;
              }}
              onMouseMove={onTimelineMove}
              onMouseLeave={() => setCursor(null)}
            >
              <div
                className="relative"
                style={{
                  width:
                    (printMode ? trackW : Math.max(trackW, rowsScrollW)) +
                    scrollGutter,
                  height: CHART_H
                }}
              >
                <div
                  className="relative bg-white"
                  data-testid="planner-milestone-chart"
                  style={{ height: CHART_H }}
                >
                  <div
                    className="absolute bg-ciq-planner-band"
                    style={{ left: 0, width, top: BAND_TOP, height: BAND_H }}
                  />
                  {issuedTo != null &&
                  ticks.length > 0 &&
                  issuedTo > ticks[0].x ? (
                    <div
                      data-testid="planner-band-issued"
                      className="absolute bg-ciq-planner-issued"
                      style={{
                        left: ticks[0].x,
                        width: issuedTo - ticks[0].x,
                        top: BAND_TOP,
                        height: BAND_H
                      }}
                    />
                  ) : null}
                  <div
                    className="absolute h-[30px] border-0 border-b border-solid border-ciq-border-color-1 bg-ciq-planner-axis"
                    style={{ left: 0, width, top: MS_HEAD_H }}
                  />
                  <div
                    className="absolute bottom-0 h-px bg-ciq-border-color-1"
                    style={{ left: 0, width }}
                  />
                  {overflowZones.map((zone) => (
                    <div key={`zone-${zone.side}`}>
                      <div
                        data-testid={`planner-zone-${zone.side}`}
                        title={ZONE_TITLE}
                        className="absolute top-0"
                        style={{
                          left: zone.start,
                          width: zone.end - zone.start,
                          height: CHART_H,
                          background: ZONE_HATCH
                        }}
                      />
                      <span
                        data-testid={`planner-zone-label-${zone.side}`}
                        title={ZONE_TITLE}
                        className="absolute flex justify-center whitespace-nowrap text-[10.5px] leading-[14px] text-ciq-planner-ink3"
                        style={{
                          left: zone.start,
                          width: zone.end - zone.start,
                          top: MS_HEAD_H + 8
                        }}
                      >
                        {zone.side === "left"
                          ? `Before ${fmtZoneEdge(timeScale?.linearStartDay ?? 0)}`
                          : `After ${fmtZoneEdge(timeScale?.linearEndDay ?? 0)}`}
                      </span>
                      <AxisBreak
                        testId={`planner-zone-break-${zone.side}`}
                        x={zone.side === "left" ? zone.end : zone.start}
                        top={MS_HEAD_H + 7}
                      />
                    </div>
                  ))}
                  {marks.map((mark) => (
                    <div
                      key={`${mark.label}-${mark.x}`}
                      data-testid={`planner-axis-mark-${mark.x}`}
                      className="absolute w-px bg-ciq-gray-300"
                      style={{ left: mark.x, top: MS_HEAD_H, height: 6 }}
                    >
                      {mark.label ? (
                        <span className="absolute left-1/2 top-2 w-12 -translate-x-1/2 text-center text-[10.5px] leading-[14px] text-ciq-planner-ink3">
                          {mark.label}
                        </span>
                      ) : null}
                    </div>
                  ))}
                  {showToday && todayX != null ? (
                    <>
                      <div
                        data-testid="planner-today-stub"
                        aria-hidden
                        className="pointer-events-none absolute z-[13] -translate-x-1/2"
                        style={{
                          left: todayX,
                          top: MS_HEAD_H,
                          width: 1,
                          height: CHART_H - MS_HEAD_H,
                          background: dashes(MARKER_INK, "bottom", 2, 2)
                        }}
                      />
                      <div
                        data-testid="planner-today"
                        className="pointer-events-none absolute z-[24] -translate-x-1/2 whitespace-nowrap rounded bg-ciq-gray-960 px-1.5 text-[10.5px] font-semibold leading-4 text-white"
                        style={{ left: todayX, top: MS_HEAD_H + 7 }}
                      >
                        Today {fmtCommitted(todayIso)}
                      </div>
                    </>
                  ) : null}
                  {ticks.map((tick, index) => {
                    const next = ticks[index + 1];
                    const days = tick.milestone.offset?.duration ?? null;
                    const left = tick.x;
                    const labelW = labelLayout.widths[index];
                    const labelTop =
                      labelSides[index] === "bottom"
                        ? MS_LABEL_BOTTOM
                        : MS_LABEL_TOP;
                    const nextGap = next ? next.x - tick.x : 0;
                    const offsetMode =
                      next && days != null && days > 0
                        ? offsetFit(nextGap)
                        : null;
                    const milestoneHovered =
                      hoveredMilestoneId === tick.milestone.id;
                    const actual = tick.milestone.actualDate;
                    const shown = actual || tick.milestone.effectiveDate;
                    // The hover card sits a little above the label; the
                    // accent line reaches up with it so their tops meet.
                    const cardTop =
                      labelSides[index] === "bottom" ? labelTop - 6 : 4;
                    const lineTop = milestoneHovered ? cardTop : labelTop;
                    return (
                      <div key={tick.milestone.id}>
                        <div
                          data-testid={`planner-ms-label-${tick.milestone.id}`}
                          className={`absolute z-[16] flex -translate-x-full flex-col items-end gap-1 overflow-hidden text-right ${
                            milestoneHovered
                              ? "rounded-l-md bg-ciq-gray-100"
                              : ""
                          }`}
                          style={
                            milestoneHovered
                              ? {
                                  left: left - 2,
                                  top: cardTop,
                                  width: labelW + 20,
                                  padding: "6px 8px 8px 12px"
                                }
                              : {
                                  left: left - 10,
                                  top: labelTop,
                                  width: labelW
                                }
                          }
                          onMouseEnter={(e) =>
                            openMilestoneCard(
                              tick.milestone.id,
                              e.currentTarget
                            )
                          }
                          onMouseLeave={scheduleMilestoneCardClose}
                        >
                          <span
                            className="max-w-full truncate text-[13px] font-bold leading-[14px] text-ciq-planner-ink"
                            title={tick.milestone.name}
                          >
                            {tick.milestone.name}
                          </span>
                          {shown ? (
                            <span className="inline-flex max-w-full items-center gap-[5px] truncate text-[11px] leading-3 text-ciq-planner-ink3">
                              {actual ? (
                                <CheckCircleOutlined className="text-[11px]" />
                              ) : null}
                              {actual ? "Actual" : "Committed"}{" "}
                              {fmtCommitted(shown)}
                            </span>
                          ) : null}
                        </div>
                        <div
                          data-testid={`planner-ms-line-${tick.milestone.id}`}
                          aria-hidden
                          className="pointer-events-none absolute w-px -translate-x-1/2 bg-ciq-planner-accent"
                          style={{
                            left,
                            top: lineTop,
                            height: PIN_Y - lineTop
                          }}
                        />
                        <div
                          data-testid={`planner-ms-line-thick-${tick.milestone.id}`}
                          aria-hidden
                          className="pointer-events-none absolute w-1 -translate-x-1/2 rounded-sm bg-ciq-planner-accent"
                          style={{
                            left,
                            top: lineTop,
                            height: MS_LABEL_BAR_H + labelTop - lineTop
                          }}
                        />
                        {next && offsetMode && offsetMode !== "pin" ? (
                          <>
                            <div
                              className="absolute h-px bg-ciq-planner-connector"
                              style={{
                                left: tick.x + 6,
                                top: OFFSET_TOP + 10,
                                width: nextGap - 12
                              }}
                            />
                            <span
                              className="absolute h-[7px] w-[7px] rotate-45 border-0 border-b border-l border-solid border-ciq-gray-400"
                              style={{ left: tick.x + 7, top: OFFSET_TOP + 7 }}
                            />
                            <span
                              className="absolute h-[7px] w-[7px] -rotate-[135deg] border-0 border-b border-l border-solid border-ciq-gray-400"
                              style={{ left: next.x - 14, top: OFFSET_TOP + 7 }}
                            />
                            <span
                              className="absolute flex -translate-x-1/2 items-center gap-1 bg-white px-0.5"
                              style={{
                                left: (tick.x + next.x) / 2,
                                top: OFFSET_TOP
                              }}
                            >
                              <span
                                data-testid={`planner-offset-${tick.milestone.id}`}
                                title={offsetText(days ?? 0, "full")}
                                className="block h-5 whitespace-nowrap rounded-[5px] border border-solid border-ciq-gray-300 bg-ciq-planner-foot px-2 text-xs leading-[18px] text-ciq-planner-ink2"
                              >
                                {offsetText(days ?? 0, offsetMode)}
                              </span>
                              {nextDelay &&
                              next.milestone.id === nextDelay.milestoneId ? (
                                <MilestoneDelayChip days={nextDelay.days} />
                              ) : null}
                            </span>
                          </>
                        ) : nextDelay &&
                          next?.milestone.id === nextDelay.milestoneId ? (
                          <span
                            className="absolute -translate-x-full"
                            style={{ left: next.x - 10, top: OFFSET_TOP }}
                          >
                            <MilestoneDelayChip days={nextDelay.days} />
                          </span>
                        ) : null}
                        {index === 0 &&
                        nextDelay &&
                        tick.milestone.id === nextDelay.milestoneId ? (
                          <span
                            className="absolute -translate-x-full"
                            style={{ left: tick.x - 10, top: OFFSET_TOP }}
                          >
                            <MilestoneDelayChip days={nextDelay.days} />
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                  {ticks.map((tick) => {
                    const count = linkCount(tick.milestone.id);
                    const chipFaded =
                      bidListFiltered &&
                      !visibleLinkedMilestones.has(tick.milestone.id);
                    const chipActive = activeMilestoneId === tick.milestone.id;
                    const milestoneHovered =
                      hoveredMilestoneId === tick.milestone.id;
                    const snapTarget =
                      dragActive && linkDrag?.targetId === tick.milestone.id;
                    const pinLit =
                      milestoneHovered ||
                      litMilestones.has(tick.milestone.id) ||
                      snapTarget;
                    const dragSource =
                      !snapTarget &&
                      dragLink?.milestoneId === tick.milestone.id;
                    return (
                      <div key={`dot-${tick.milestone.id}`}>
                        {snapTarget ? (
                          <span
                            className="pointer-events-none absolute z-20 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ciq-planner-accent/[0.18]"
                            style={{ left: tick.x, top: PIN_Y }}
                          />
                        ) : null}
                        {dragActive ? (
                          <span
                            data-testid={`planner-ms-target-${tick.milestone.id}`}
                            className={`pointer-events-none absolute z-20 h-[22px] w-[22px] -translate-x-1/2 -translate-y-1/2 rounded-full border-ciq-planner-accent ${
                              snapTarget
                                ? "border-2 border-solid"
                                : `border-[1.5px] border-dashed ${linkDrag?.targetId ? "opacity-50" : ""}`
                            }`}
                            style={{ left: tick.x, top: PIN_Y }}
                          />
                        ) : null}
                        {dragActive && dragSource ? (
                          <span
                            className="pointer-events-none absolute z-20 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-ciq-planner-accent opacity-40"
                            style={{ left: tick.x, top: PIN_Y }}
                          />
                        ) : null}
                        {pinLit && !dragActive ? (
                          <span
                            className="pointer-events-none absolute z-20 flex h-[31px] w-[31px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-ciq-planner-accent/[0.12]"
                            style={{ left: tick.x, top: PIN_Y }}
                          >
                            <span className="h-[25px] w-[25px] rounded-full border-[3px] border-dotted border-ciq-planner-accent/20" />
                          </span>
                        ) : null}
                        <div
                          data-testid={`planner-ms-dot-${tick.milestone.id}`}
                          title={
                            offsetInPin.has(tick.milestone.id)
                              ? offsetText(
                                  offsetInPin.get(tick.milestone.id) ?? 0,
                                  "full"
                                )
                              : undefined
                          }
                          data-complete={
                            tick.milestone.actualDate ? true : undefined
                          }
                          className={`absolute z-20 -translate-x-1/2 -translate-y-1/2 cursor-pointer border-2 border-solid ${
                            offsetInPin.has(tick.milestone.id)
                              ? "flex h-6 min-w-6 items-center justify-center rounded-full border-white bg-ciq-planner-accent px-1 text-[11px] font-semibold leading-none text-white"
                              : tick.milestone.actualDate
                                ? // Complete: a tick in an outlined circle.
                                  "flex h-4 w-4 items-center justify-center rounded-full border-ciq-planner-accent bg-white text-[8px] text-ciq-planner-accent"
                                : "h-4 w-4 rounded-full border-white bg-ciq-planner-accent"
                          }`}
                          style={{ left: tick.x, top: PIN_Y }}
                          onMouseEnter={(e) =>
                            openMilestoneCard(
                              tick.milestone.id,
                              e.currentTarget
                            )
                          }
                          onMouseLeave={scheduleMilestoneCardClose}
                        >
                          {offsetInPin.has(tick.milestone.id) ? (
                            offsetInPin.get(tick.milestone.id)
                          ) : tick.milestone.actualDate ? (
                            <CheckOutlined
                              data-testid={`planner-ms-complete-${tick.milestone.id}`}
                              aria-label="Complete"
                            />
                          ) : null}
                        </div>
                        <button
                          type="button"
                          data-testid={`planner-ms-link-count-${tick.milestone.id}`}
                          data-ms-link-count=""
                          aria-label={`${count} Bid Package${count === 1 ? "" : "s"} linked to ${tick.milestone.name}. Highlight its links`}
                          aria-pressed={chipActive}
                          // Nothing to highlight without a visible link.
                          disabled={
                            !visibleLinkedMilestones.has(tick.milestone.id)
                          }
                          data-faded={chipFaded || undefined}
                          className={`absolute z-20 flex h-[22px] -translate-x-1/2 cursor-pointer items-center gap-[3px] whitespace-nowrap rounded-[5px] border border-solid px-[5px] text-[11px] font-medium leading-5 transition-[opacity,background-color,border-color,color] duration-150 disabled:cursor-default ${
                            chipActive
                              ? "border-ciq-planner-accent bg-ciq-planner-accent text-white"
                              : "border-ciq-gray-200 bg-white text-ciq-planner-ink2 hover:border-ciq-planner-accent disabled:hover:border-ciq-gray-200"
                          } ${chipFaded ? "opacity-50" : ""}`}
                          style={{ left: tick.x, top: CHIP_Y }}
                          onClick={() =>
                            setActiveMilestoneId((current) =>
                              current === tick.milestone.id
                                ? null
                                : tick.milestone.id
                            )
                          }
                        >
                          <LinkOutlined
                            className={`text-xs ${chipActive ? "text-white" : "text-black/[0.87]"}`}
                          />
                          {count}
                        </button>
                      </div>
                    );
                  })}
                  {startLines.map((line) => (
                    <div
                      key={`chart-start-${line.id}`}
                      data-testid={`planner-bp-start-stub-${line.id}`}
                      aria-hidden
                      className="pointer-events-none absolute z-[11] -translate-x-1/2"
                      style={{
                        left: line.x,
                        top: AXIS_BOTTOM,
                        width: START_LINE_W,
                        height: CHART_H - AXIS_BOTTOM,
                        background: dashes(
                          line.color,
                          "bottom",
                          ...START_LINE_DASH
                        )
                      }}
                    />
                  ))}
                  {drawnLinks.map(({ g, mx, milestoneId }) => {
                    const id = g.row.id;
                    if (dragActive && linkDrag?.bpId === id) return null;
                    const lit = litIds.has(id);
                    const paint = bidLinkPaint(g.row, lit);
                    return (
                      <div
                        key={`chart-stem-${id}`}
                        data-testid={`planner-bp-chart-stem-${id}`}
                        aria-hidden
                        className="pointer-events-none absolute -translate-x-1/2 transition-opacity duration-150"
                        style={{
                          zIndex: lit ? 13 : 12,
                          left: mx,
                          top: PIN_Y,
                          width: paint.w,
                          height: CHART_H - PIN_Y,
                          opacity: linkOpacity(id, milestoneId),
                          background: paint.dash
                            ? dashes(
                                paint.color,
                                "bottom",
                                paint.dash[0],
                                paint.dash[1]
                              )
                            : paint.color
                        }}
                      />
                    );
                  })}
                  {dragCaution && dragTick ? (
                    <div
                      aria-hidden
                      className="pointer-events-none absolute z-[12] w-0.5 -translate-x-1/2"
                      style={{
                        left: dragTick.x,
                        top: PIN_Y,
                        height: CHART_H - PIN_Y,
                        background: dashes("var(--ciq-red-400)", "bottom", 5, 4)
                      }}
                    />
                  ) : null}
                  {relink && !activeDrag ? (
                    <button
                      type="button"
                      data-testid={`planner-bp-link-handle-${relink.g.row.id}`}
                      aria-label={`Drag milestone end for ${relink.g.row.name || relink.g.row.number}`}
                      title="Drag to change the milestone"
                      className="absolute z-40 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 cursor-grab items-center justify-center rounded-full border-none bg-ciq-planner-accent/10 p-0"
                      style={{ left: relink.mx, top: PIN_Y }}
                      onMouseEnter={() =>
                        hoverLink({ type: "link", id: relink.g.row.id })
                      }
                      onMouseLeave={() => hoverLink(null)}
                      onPointerDown={(event) =>
                        onLinkHandleDown(relink.g.row.id, event)
                      }
                    >
                      <span className="h-3.5 w-3.5 rounded-full border-2 border-solid border-ciq-planner-accent bg-white" />
                    </button>
                  ) : null}
                </div>
                {cursor ? (
                  <div
                    data-testid="planner-date-cursor"
                    className="pointer-events-none absolute z-30 -translate-x-1/2 rounded-full bg-ciq-primary-900 px-2 py-0.5 text-[10px] font-semibold text-white"
                    style={{ left: cursor.left, top: BAND_TOP - 4 }}
                  >
                    {cursor.label}
                  </div>
                ) : null}
              </div>
            </div>
            <div
              ref={rowsScrollRef}
              className={
                printMode
                  ? "min-h-0 flex-1 overflow-visible"
                  : `min-h-0 min-w-0 flex-1 overflow-x-auto ${
                      rosScrolls ? "overflow-y-hidden" : "overflow-y-auto"
                    } overscroll-x-none`
              }
              data-testid="planner-timeline-scroll"
              style={{ marginRight: rosW }}
              onScroll={() => syncBidScroll("rows")}
              onWheel={(event) => {
                // Its own vertical scrollbar is hidden while Required on site
                // carries it, so the wheel scrolls the rows directly.
                if (!rosScrolls || event.shiftKey || event.deltaY === 0) return;
                event.currentTarget.scrollTop += event.deltaY;
              }}
              onMouseMove={onTimelineMove}
              onMouseLeave={() => setCursor(null)}
            >
              <div
                ref={trackRef}
                className="relative"
                data-testid="planner-timeline-track"
                style={{
                  width: trackW,
                  marginTop: -CHART_H
                }}
              >
                <div aria-hidden style={{ height: CHART_H }} />
                {overflowZones.map((zone) => (
                  <div
                    key={`zone-rows-${zone.side}`}
                    data-testid={`planner-zone-rows-${zone.side}`}
                    title={ZONE_TITLE}
                    className="absolute bottom-0"
                    style={{
                      left: zone.start,
                      width: zone.end - zone.start,
                      top: CHART_H,
                      background: ZONE_HATCH
                    }}
                  />
                ))}
                {marks.map((mark) => (
                  <div
                    key={`grid-${mark.x}`}
                    className="pointer-events-none absolute bottom-0 w-px bg-ciq-planner-grid"
                    style={{ left: mark.x, top: MS_HEAD_H + 30 }}
                  />
                ))}
                {showToday && todayX != null ? (
                  <div
                    data-testid="planner-today-line"
                    aria-hidden
                    className="pointer-events-none absolute bottom-0 z-[3] -translate-x-1/2"
                    style={{
                      left: todayX,
                      top: CHART_H,
                      width: 1,
                      background: dashes(MARKER_INK, "bottom", 2, 2)
                    }}
                  />
                ) : null}
                {startLines.map((line) => (
                  <div
                    key={`start-${line.id}`}
                    data-testid={`planner-bp-start-line-${line.id}`}
                    aria-hidden
                    className="pointer-events-none absolute z-[1] -translate-x-1/2"
                    style={{
                      left: line.x,
                      top: CHART_H,
                      width: START_LINE_W,
                      height: line.cardTop - CHART_H,
                      background: dashes(
                        line.color,
                        "bottom",
                        ...START_LINE_DASH
                      )
                    }}
                  />
                ))}
                {drawnLinks.map(({ g, mx, bar, milestoneId }) => {
                  const id = g.row.id;
                  if (dragActive && linkDrag?.bpId === id) return null;
                  const lit = litIds.has(id);
                  const pts = linkPoints(mx, bar, lit ? PIN_Y : REST_TOP);
                  const end = pts[pts.length - 1];
                  const hitPts = linkPoints(
                    mx,
                    bar,
                    lit ? PIN_Y + 18 : REST_TOP
                  );
                  const paint = bidLinkPaint(g.row, lit);
                  const relinking = relink?.g.row.id === id;
                  return (
                    <div key={`link-${id}`}>
                      {segments(id, pts, {
                        color: paint.color,
                        lineW: paint.w,
                        dash: paint.dash,
                        opacity: linkOpacity(id, milestoneId),
                        z: lit ? 15 : 1
                      })}
                      {hitPts.slice(1).map((p, i) => {
                        const [x1, y1] = hitPts[i];
                        const [x2, y2] = p;
                        const vertical = x1 === x2;
                        return (
                          <div
                            key={`hit-${i}`}
                            className={`absolute z-[12] ${vertical ? "-translate-x-1/2" : "-translate-y-1/2"}`}
                            style={
                              vertical
                                ? {
                                    left: x1,
                                    top: Math.min(y1, y2),
                                    width: 14,
                                    height: Math.abs(y2 - y1)
                                  }
                                : {
                                    left: Math.min(x1, x2),
                                    top: y1,
                                    width: Math.abs(x2 - x1),
                                    height: 14
                                  }
                            }
                            {...linkHoverHandlers(id)}
                          />
                        );
                      })}
                      {/* While re-linking, the card handle on the card's
                            start is the only ball at this end. */}
                      {relinking ? null : (
                        <span
                          data-testid={`planner-bp-link-dot-${id}`}
                          className={`pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-1/2 rounded-full ${
                            lit
                              ? "h-[7px] w-[7px] border border-solid border-white"
                              : "h-1.5 w-1.5"
                          }`}
                          style={{
                            left: end[0],
                            top: end[1],
                            background: paint.color,
                            opacity: linkOpacity(id, milestoneId),
                            transition: "opacity 150ms"
                          }}
                        />
                      )}
                    </div>
                  );
                })}
                {dragActive && linkDrag && dragLink ? dragOverlay() : null}
                {geometry.rows.length === 0 ? (
                  <div className="relative min-h-[280px]">
                    <div
                      className="absolute top-6 flex w-[420px] max-w-full -translate-x-1/2 flex-col items-center px-8 text-center"
                      style={{ left: timelineCenter }}
                    >
                      <img
                        src={emptyBidPackagesIllustration}
                        alt=""
                        width={160}
                        height={74}
                        className="mb-4 h-[74px] w-40"
                      />
                      <div className="text-base font-semibold text-black">
                        No Bid Packages linked
                      </div>
                      <p className="mt-1 max-w-[400px] text-[13px] text-ciq-gray-700">
                        Link Bid Packages to design milestones to plan and track
                        trade buyout dates.
                      </p>

                      {onManageBidPackages ? (
                        <Button
                          type="primary"
                          data-testid="planner-empty-manage-bid-packages"
                          className=""
                          onClick={onManageBidPackages}
                        >
                          Link Bid Packages
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  rows.map((g) => {
                    const card = placeCard(g);
                    const tone = bidCardTone(g.row);
                    const selected = g.row.id === detailBidId;
                    const highlighted = highlightOn && litIds.has(g.row.id);
                    const dimmed = highlightOn && !highlighted;
                    return (
                      <div
                        key={g.row.id}
                        // Ends in the row id, so a press anywhere on the
                        // selected package's row is kept, not a press outside.
                        data-testid={`planner-bp-lane-${g.row.id}`}
                        className={`relative border-0 border-b border-solid border-ciq-planner-row-line ${rowBg(
                          g.row.id
                        )}`}
                        style={{ height: BID_ROW_H }}
                        {...rowHoverHandlers(g.row.id)}
                      >
                        {card ? (
                          // 10px sides / 6px top in the design, measured from
                          // the outer edge, so less the 1px border. A card with
                          // no plan has 8px above and below, its two lines
                          // pushed to the top and bottom edges.
                          <div
                            data-selected={selected || undefined}
                            className={`absolute z-10 flex cursor-pointer flex-col gap-1 overflow-hidden rounded-lg px-[9px] ${
                              bidCardHasNoPlan(g.row, card.unplanned)
                                ? "justify-between py-[7px]"
                                : "pt-[5px]"
                            } ${tone.card} ${selected ? SELECTED_CARD : ""}`}
                            data-testid={`${BID_BAR_TESTID}${g.row.id}`}
                            data-highlighted={highlighted || undefined}
                            data-dimmed={dimmed || undefined}
                            role="button"
                            tabIndex={0}
                            style={{
                              left: card.x,
                              top: CARD_DY,
                              width: card.width,
                              height: CARD_H,
                              opacity: dimmed ? HIGHLIGHT_DIM : 1,
                              transition: "opacity 150ms, box-shadow 150ms",
                              // A halo in its link's highlighted colour.
                              ...(highlighted && !selected
                                ? {
                                    boxShadow: `0 0 0 3px color-mix(in srgb, ${
                                      bidLinkPaint(g.row, true).color
                                    } 25%, transparent)`
                                  }
                                : {})
                            }}
                            onClick={(event) => {
                              if ((event.target as HTMLElement).closest("a"))
                                return;
                              if (!linkDrag) selectBid(g.row.id);
                            }}
                            onKeyDown={(event) => {
                              if (event.key !== "Enter" && event.key !== " ")
                                return;
                              if ((event.target as HTMLElement).closest("a"))
                                return;
                              event.preventDefault();
                              if (!linkDrag) selectBid(g.row.id);
                            }}
                          >
                            <BidCardBody
                              row={g.row}
                              unplanned={card.unplanned}
                              onCreatePlan={
                                onCreatePlan
                                  ? () => onCreatePlan(g.row.id)
                                  : undefined
                              }
                            />
                            {card.continuesLeft ? (
                              <CardContinues
                                side="left"
                                id={g.row.id}
                                color={tone.edge}
                              />
                            ) : null}
                            {card.continuesRight ? (
                              <CardContinues
                                side="right"
                                id={g.row.id}
                                color={tone.edge}
                              />
                            ) : null}
                          </div>
                        ) : null}
                        {card && timeScale ? (
                          <BidRowMarkers
                            row={g.row}
                            scale={timeScale}
                            cardRight={card.x + card.width}
                            faded={dimmed}
                          />
                        ) : null}
                        {card ? (
                          <div
                            data-testid={`planner-ros-link-${g.row.id}`}
                            aria-hidden
                            className="pointer-events-none absolute z-[4] h-px -translate-y-1/2"
                            style={{
                              left: card.x + card.width,
                              top: CARD_DY + CARD_H / 2,
                              // High risk or late: the line to Required on site
                              // is the same light red as that card's border.
                              background: bidIsAlert(g.row)
                                ? ALERT_SITE_COLOR
                                : "var(--ciq-gray-300)",
                              // Absolutely positioned, so it counts towards the
                              // scroller's scrollWidth: past the track it holds
                              // the scroll area open over a range the timeline
                              // no longer draws.
                              width: Math.max(
                                0,
                                Math.min(
                                  viewRight > 0 ? viewRight : width + 12,
                                  trackW
                                ) -
                                  (card.x + card.width)
                              )
                            }}
                          />
                        ) : null}
                      </div>
                    );
                  })
                )}
                {cursor ? (
                  <div
                    className="pointer-events-none absolute bottom-0 top-0 w-px bg-ciq-primary-900/60"
                    style={{ left: cursor.left }}
                  />
                ) : null}
              </div>
            </div>
            {hiddenAbove > 0 ? (
              <button
                type="button"
                data-testid="planner-bp-hidden-above"
                aria-label={`${hiddenAbove} more Bid Package${hiddenAbove === 1 ? "" : "s"} above`}
                className="absolute left-3 z-30 inline-flex h-[22px] cursor-pointer items-center gap-1 whitespace-nowrap rounded-[5px] border border-solid border-ciq-warning-text bg-ciq-warning-card px-2 text-[11px] font-semibold leading-5 !text-ciq-planner-overflow-ink shadow-[0_2px_6px_rgba(0,0,0,0.12)]"
                style={{ top: CHART_H + 8 }}
                onClick={() => scrollRowIntoView(lastAboveIndex, "top")}
              >
                + {hiddenAbove}
                <ArrowUpOutlined className="text-[10px] !text-ciq-planner-overflow-ink" />
              </button>
            ) : null}
            {hiddenBelow > 0 ? (
              <button
                type="button"
                data-testid="planner-bp-hidden-below"
                aria-label={`${hiddenBelow} more Bid Package${hiddenBelow === 1 ? "" : "s"} below`}
                className="absolute bottom-5 left-3 z-30 inline-flex h-[22px] cursor-pointer items-center gap-1 whitespace-nowrap rounded-[5px] border border-solid border-ciq-warning-text bg-ciq-warning-card px-2 text-[11px] font-semibold leading-5 !text-ciq-planner-overflow-ink shadow-[0_2px_6px_rgba(0,0,0,0.12)]"
                onClick={() => scrollRowIntoView(firstBelowIndex, "bottom")}
              >
                + {hiddenBelow}
                <ArrowDownOutlined className="text-[10px] !text-ciq-planner-overflow-ink" />
              </button>
            ) : null}
            {rosShown && !rosOpen ? (
              <div
                data-testid="planner-required-on-site-collapsed"
                className="absolute bottom-0 right-0 z-20 flex flex-col border-0 border-l border-solid border-ciq-gray-200 bg-ciq-gray-25"
                style={{ width: ROS_COLLAPSED_W, top: 0 }}
              >
                <div className="relative shrink-0" style={{ height: CHART_H }}>
                  <Tooltip title="Show Required on site" placement="left">
                    <button
                      type="button"
                      aria-label="Show Required on site"
                      data-testid="planner-ros-toggle"
                      className={`${ROS_TOGGLE} left-1/2 -translate-x-1/2`}
                      style={{ top: ROS_HEADER_TOP }}
                      onClick={() => setRosCollapsed(false)}
                    >
                      <DoubleLeftOutlined className="text-sm" />
                    </button>
                  </Tooltip>
                </div>
                {/* Tucked away, the rail still carries the rows' vertical
                    scrollbar, so the wheel scrolls them from here too. */}
                <div className="relative min-h-0 flex-1">
                  <div
                    ref={rosListRef}
                    data-testid="planner-ros-rail-scroll"
                    // A native scrollbar (~17px on Windows) would fill most
                    // of the 28px rail and cover its label, so the rail
                    // draws a 4px one on its right edge, as designed.
                    // Chromium ignores ::-webkit-scrollbar once
                    // scrollbar-width is set, so the thin standard
                    // scrollbar applies only where those rules are not
                    // supported (Firefox).
                    className="h-full overflow-y-auto overscroll-x-none [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-ciq-gray-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1 supports-[not_selector(::-webkit-scrollbar)]:[scrollbar-color:var(--ciq-gray-300)_transparent] supports-[not_selector(::-webkit-scrollbar)]:[scrollbar-width:thin]"
                    onScroll={() => syncBidScroll("ros")}
                    onWheel={(event) => {
                      const rowScroller = rowsScrollRef.current;
                      if (!rowScroller) return;
                      rowScroller.scrollLeft += event.deltaX;
                    }}
                  >
                    <div
                      aria-hidden
                      style={{
                        height:
                          rows.length * BID_ROW_H +
                          (rows.length > 0 ? rowsScrollbarH : 0)
                      }}
                    />
                  </div>
                  <span
                    aria-hidden
                    // Centred in the rail left of its 4px scrollbar.
                    className="pointer-events-none absolute left-3 top-[15px] -translate-x-1/2 whitespace-nowrap text-[11px] leading-3 text-ciq-planner-ink4 [writing-mode:vertical-rl]"
                  >
                    Lead time &amp; On-site
                  </span>
                </div>
              </div>
            ) : null}
            {rosOpen ? (
              <div
                className="absolute bottom-0 right-0 z-20 flex flex-col border-0 border-l border-solid border-ciq-gray-300 bg-white"
                style={{ width: ROS_COL_W, top: 0 }}
              >
                <div className="relative shrink-0" style={{ height: CHART_H }}>
                  {/* In the Bid Packages header row, level with the link
                      counters, as the rows below it start there. */}
                  <div
                    data-testid="planner-required-on-site"
                    className="absolute flex items-center font-semibold leading-[22px] text-ciq-planner-ink4"
                    style={{
                      left: 16,
                      top: ROS_HEADER_TOP,
                      width: ROS_COL_W - 56
                    }}
                  >
                    Required on site
                  </div>
                  {printMode ? null : (
                    <Tooltip title="Hide Required on site" placement="left">
                      <button
                        type="button"
                        aria-label="Hide Required on site"
                        data-testid="planner-ros-toggle"
                        className={`${ROS_TOGGLE} right-2`}
                        style={{ top: ROS_HEADER_TOP }}
                        onClick={() => setRosCollapsed(true)}
                      >
                        <DoubleRightOutlined className="text-sm" />
                      </button>
                    </Tooltip>
                  )}
                </div>
                <div
                  ref={rosListRef}
                  className={`min-h-0 flex-1 overscroll-x-none ${
                    rosScrolls ? "overflow-y-auto" : "overflow-y-hidden"
                  }`}
                  onScroll={() => syncBidScroll("ros")}
                  onWheel={(event) => {
                    // Vertical wheel scrolls this list natively; sideways
                    // goes to the timeline.
                    const rowScroller = rowsScrollRef.current;
                    if (!rowScroller) return;
                    rowScroller.scrollLeft += event.deltaX;
                  }}
                >
                  {rows.map((g) => (
                    <div
                      key={g.row.id}
                      data-testid={`planner-ros-${g.row.id}`}
                      className={`relative ${rowBg(g.row.id)}`}
                      style={{ height: BID_ROW_H }}
                      {...rowHoverHandlers(g.row.id)}
                    >
                      {/* The card's header row (ROS_HEAD_H) sits above it, so
                          the card itself is centred on the bid card. */}
                      <div
                        className="absolute left-0 right-0"
                        style={{
                          top: CARD_DY + (CARD_H - ROS_CARD_H) / 2 - ROS_HEAD_H
                        }}
                      >
                        <div className="min-w-0 px-3">
                          <BidPackageRequiredOnSite
                            bidPackageId={g.row.id}
                            projectId={projectId}
                            unplanned={bidIsUnplanned(g.row)}
                            alert={bidIsAlert(g.row)}
                            onSetDate={
                              onSetRequiredOnSite
                                ? (isoDate) =>
                                    onSetRequiredOnSite(g.row.id, isoDate)
                                : undefined
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {rows.length > 0 && rosScrolls && rowsScrollbarH > 0 ? (
                    <div aria-hidden style={{ height: rowsScrollbarH }} />
                  ) : null}
                </div>
              </div>
            ) : null}
            {/* Inside the timeline: from the milestone header down to the
                legend, under the design package card's header. */}
            {detailOpen && detailRow ? (
              <div className="absolute bottom-0 right-0 top-0 z-20">
                <PlannerBidDetailDrawer
                  bidPackageId={detailRow.row.id}
                  scopeCount={bidScopeItems?.[detailRow.row.id]?.length ?? 0}
                  label={bidPackageLabel(
                    detailRow.row.number,
                    detailRow.row.name
                  )}
                  milestoneName={detailRow.row.linkedDpMilestone?.name}
                  effectiveFloat={detailRow.row.effectiveFloat}
                  projectId={projectId}
                  onOpenScope={onBidScopeOpen}
                  onHide={() => setDetailPanelOpen(false)}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex h-9 shrink-0 items-stretch border-0 border-t border-solid border-ciq-gray-100 bg-ciq-planner-foot">
          {/* Full width from the panel's left edge: swatch groups on the
              left, the Show switches pushed to the right. */}
          <div
            data-testid="planner-legend"
            className="flex min-w-0 flex-1 items-center gap-3 px-4 text-[11px] text-ciq-planner-ink3"
          >
            {footerSlot ? (
              <div className="flex shrink-0 items-center">{footerSlot}</div>
            ) : null}
            {/* Scrolls sideways when the window is too narrow, with the
                scrollbar hidden. A plain mouse wheel scrolls it too. */}
            <div
              data-testid="planner-legend-swatches"
              className="flex min-w-0 items-center gap-2 overflow-x-auto overflow-y-hidden overscroll-x-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              onWheel={(event) => {
                if (event.deltaX !== 0 || event.deltaY === 0) return;
                event.currentTarget.scrollLeft += event.deltaY;
              }}
            >
              <LegendGroup>
                <LegendItem label="Design milestone">
                  <span className="h-[11px] w-[11px] rounded-full bg-ciq-planner-accent" />
                </LegendItem>
                <LegendItem label="Bid Package">
                  <span className="h-[13px] w-[22px] rounded-[3px] border border-dashed border-ciq-warning-text bg-ciq-warning-card" />
                </LegendItem>
                <LegendItem label="Running late">
                  <span className="h-[13px] w-[22px] rounded-[3px] border border-solid border-ciq-error-500 bg-ciq-warning-card" />
                </LegendItem>
              </LegendGroup>
              <LegendGroup>
                <LegendItem label="Link">
                  <span className="h-0.5 w-5 bg-ciq-planner-ink3" />
                </LegendItem>
                <LegendItem label="Link on hover">
                  <span
                    className="h-0.5 w-5"
                    style={{ background: dashes(LINK_COLOR, "right", 4, 3) }}
                  />
                </LegendItem>
                <LegendItem label="Bid start line">
                  <span
                    className="h-0.5 w-5"
                    style={{
                      background: dashes(START_LINE_COLOR, "right", 4, 3)
                    }}
                  />
                </LegendItem>
                <LegendItem label="High Risk">
                  <span className="h-0.5 w-5 bg-ciq-error-500" />
                </LegendItem>
              </LegendGroup>
              <LegendGroup>
                <LegendItem label="Projected">
                  <span className="h-3.5 w-0.5 bg-ciq-planner-ink" />
                </LegendItem>
                <LegendItem label="Deadline">
                  <span className="h-3.5 w-0.5 bg-ciq-warning-text" />
                </LegendItem>
              </LegendGroup>
            </div>
            {printMode ? null : (
              <LegendGroup className="ml-auto">
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-semibold">
                  Show
                  <Tooltip title="Choose what is drawn on the timeline">
                    <InfoCircleOutlined
                      data-testid="planner-legend-show-info"
                      className="text-xs text-ciq-planner-ink3"
                    />
                  </Tooltip>
                </span>
                <LegendSwitch
                  testId="planner-show-start-lines"
                  label="Start lines"
                  checked={showStartLines}
                  onChange={setShowStartLines}
                />
                <LegendSwitch
                  testId="planner-show-link-lines"
                  label="Link Lines"
                  checked={showLinkLines}
                  onChange={setShowLinkLines}
                />
                {crosshair ? (
                  <LegendSwitch
                    testId="planner-show-date-cursor"
                    label="Date under cursor"
                    checked={showDateCursor}
                    onChange={setShowDateCursor}
                  />
                ) : null}
              </LegendGroup>
            )}
          </div>
        </div>
        {hoverCard && hoveredMilestone
          ? createPortal(
              <div
                data-testid="planner-ms-hover-card"
                className="fixed z-50 -translate-x-1/2"
                style={{ left: hoverCard.left, top: hoverCard.top }}
                onMouseEnter={clearCardClose}
                onMouseLeave={scheduleMilestoneCardClose}
              >
                <MilestoneHoverCard
                  key={hoveredMilestone.id}
                  milestone={hoveredMilestone}
                  milestones={milestones}
                  linkedCount={linkCount(hoveredMilestone.id)}
                  scopeCount={
                    milestoneScopeItems?.[hoveredMilestone.id]?.length ?? 0
                  }
                  onRename={
                    onRenameMilestone
                      ? (name) => onRenameMilestone(hoveredMilestone.id, name)
                      : undefined
                  }
                  onDelete={
                    onDeleteMilestone
                      ? () => {
                          setDeletingMilestone(hoveredMilestone);
                          hoverMilestoneIdRef.current = null;
                          cardLockedRef.current = false;
                          setHoverCard(null);
                          setHoveredMilestoneId(null);
                        }
                      : undefined
                  }
                  canDelete={milestones.length > 2}
                  onCommittedDate={
                    onMilestoneReschedule
                      ? (isoDate) =>
                          onMilestoneReschedule(hoveredMilestone.id, isoDate)
                      : undefined
                  }
                  onActualDate={
                    onMilestoneActualDate
                      ? (isoDate) =>
                          onMilestoneActualDate(hoveredMilestone.id, isoDate)
                      : undefined
                  }
                  onOpenScope={
                    onMilestoneScopeOpen
                      ? () =>
                          onMilestoneScopeOpen({
                            milestoneId: hoveredMilestone.id,
                            label: hoveredMilestone.name
                          })
                      : undefined
                  }
                  onLockChange={(locked) => {
                    cardLockedRef.current = locked;
                  }}
                />
              </div>,
              portalTarget
            )
          : null}
        {cardLink && trackBox
          ? createPortal(
              <>
                {(() => {
                  if (!showLinkLines) return null;
                  const pts = linkPoints(cardLink.mx, cardLink.bar, PIN_Y);
                  const end = pts[pts.length - 1];
                  const prev = pts[pts.length - 2];
                  if (!prev || prev[1] !== end[1]) return null;
                  const rowsBox =
                    rowsScrollRef.current?.getBoundingClientRect();
                  const lineTop = trackBox.top + end[1];
                  if (
                    rowsBox?.height &&
                    (lineTop < rowsBox.top || lineTop > rowsBox.bottom)
                  ) {
                    return null;
                  }
                  // Painted like the rest of its (lit) link: same colour and
                  // width, so a high-risk link stays red and even.
                  const paint = bidLinkPaint(cardLink.g.row, true);
                  const lineLeft = Math.min(prev[0], end[0]);
                  let lineX = trackBox.left + lineLeft;
                  let lineW = Math.abs(end[0] - prev[0]);
                  if (rowsBox?.width) {
                    const right = Math.min(lineX + lineW, rowsBox.right);
                    lineX = Math.max(lineX, rowsBox.left);
                    lineW = right - lineX;
                    if (lineW <= 0) return null;
                  }
                  return (
                    <div
                      aria-hidden
                      className="pointer-events-none fixed z-[60] -translate-y-1/2"
                      style={{
                        left: lineX,
                        top: trackBox.top + end[1],
                        width: lineW,
                        height: paint.w,
                        background: paint.dash
                          ? dashes(paint.color, "right", ...paint.dash)
                          : paint.color
                      }}
                    />
                  );
                })()}
                {(() => {
                  const [handleX, handleY] = cardHandlePoint(cardLink.bar);
                  const ball = {
                    x: trackBox.left + handleX,
                    y: trackBox.top + handleY
                  };
                  // A selected card keeps its ball; once that card is scrolled
                  // out of the rows pane, the ball must not float over the rest
                  // of the page.
                  const rowsBox =
                    rowsScrollRef.current?.getBoundingClientRect();
                  if (
                    rowsBox?.height &&
                    (ball.x < rowsBox.left ||
                      ball.x > rowsBox.right ||
                      ball.y < rowsBox.top ||
                      ball.y > rowsBox.bottom)
                  ) {
                    return null;
                  }
                  const showHint =
                    relinkedId !== cardLink.g.row.id &&
                    hoveredBallId === cardLink.g.row.id;
                  return (
                    <>
                      <button
                        type="button"
                        data-testid={`planner-bp-card-handle-${cardLink.g.row.id}`}
                        aria-label={`Drag to change the milestone for ${cardLink.g.row.name || cardLink.g.row.number}`}
                        className="fixed z-[70] flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 cursor-grab items-center justify-center rounded-full border-none bg-ciq-planner-accent/20 p-0 active:cursor-grabbing"
                        style={{ left: ball.x, top: ball.y }}
                        onMouseEnter={() => {
                          // Moving from the link to the ball keeps it lit.
                          hoverLink({ type: "card", id: cardLink.g.row.id });
                          setHoveredBallId(cardLink.g.row.id);
                        }}
                        onMouseLeave={() => {
                          hoverLink(null);
                          setHoveredBallId(null);
                        }}
                        onPointerDown={(event) =>
                          onLinkHandleDown(cardLink.g.row.id, event)
                        }
                      >
                        <span className="h-3.5 w-3.5 rounded-full border-2 border-solid border-white bg-ciq-planner-accent shadow-[0_1px_4px_rgba(0,0,0,0.35)]" />
                      </button>
                      <div className="pointer-events-none fixed inset-0 z-[70]">
                        {showHint ? (
                          <LinkChip
                            kind="dark"
                            above="center"
                            caret
                            testId={`planner-bp-card-hint-${cardLink.g.row.id}`}
                            x={ball.x}
                            y={ball.y - 20}
                            title="Drag to change the milestone"
                          />
                        ) : null}
                      </div>
                    </>
                  );
                })()}
              </>,
              portalTarget
            )
          : null}
        {deletingMilestone ? (
          <DeleteDesignPackageMilestoneModal
            open
            milestoneName={deletingMilestone.name}
            designPackageName={designPackageName}
            linkedBids={geometry.rows
              .filter((g) => g.linkBadge?.milestoneId === deletingMilestone.id)
              .map((g) => ({
                id: g.row.id,
                label: bidPackageLabel(g.row.number, g.row.name)
              }))}
            scopeCount={
              milestoneScopeItems?.[deletingMilestone.id]?.length ?? 0
            }
            otherMilestones={(() => {
              const idx = milestones.findIndex(
                (m) => m.id === deletingMilestone.id
              );
              return [
                ...milestones.slice(idx + 1),
                ...milestones.slice(0, Math.max(idx, 0))
              ]
                .filter((m) => m.id !== deletingMilestone.id && m.name)
                .map((m) => ({
                  id: m.id,
                  name: m.name,
                  committedDate: m.committedDate || m.effectiveDate
                }));
            })()}
            confirmLoading={deleting}
            onClose={() => {
              if (!deleting) setDeletingMilestone(null);
            }}
            onConfirm={async (options) => {
              setDeleting(true);
              try {
                await onDeleteMilestone?.(deletingMilestone.id, options);
                setDeletingMilestone(null);
              } finally {
                setDeleting(false);
              }
            }}
          />
        ) : null}
      </div>
    );
  }
  return plannerLaneBody();
}
